import productSplitService from '../../../../services/ProductSplitService';
import { extractMaximumOrderQuantityFields } from "../../common/maximumOrderQuantity";
import { isCartLineOfferDisplayRole } from "../../Promotion/utils/promotionOfferProductsShared";
import Config from "../../../common/Config";

export const isMeatsProduct = (item) => {
  const raw = String(
    item?.categorie_name ??
    item?.category_name ??
    item?.category ??
    ""
  ).trim();
  if (!raw) return false;
  const u = raw.toUpperCase();
  if (u === "MEATS" || u === "MEAT") return true;
  return /\bMEATS\b/.test(u) || /\bMEAT\b/.test(u);
};

export const allowsDecimalQty = (item) => isMeatsProduct(item);

export const resolveProductTaxCode = (item) => {
  if (!item) return "";
  const raw =
    item.tax_code ??
    item.taxCode ??
    item.vat_code ??
    item.vatCode ??
    "";
  if (raw === undefined || raw === null) return "";
  const s = String(raw).trim();
  return s && s.toLowerCase() !== "null" ? s : "";
};

const CART_ITEM_NORM_CACHE_LIMIT = 500;
const cartItemNormCache = new Map();

const EMPTY_CART_ITEM_NORM = Object.freeze({
  revision: "",
  qty: 0,
  orderQty: 0,
  allotQty: 0,
  isMeats: false,
  orderType: "Box",
  isPiece: false,
  price: 0,
  unitPrice: NaN,
  cartPrice: NaN,
  originalPriceRaw: NaN,
  promoUnitPrice: NaN,
  overridePrice: NaN,
  salesBoxPrice: 0,
  salesPiecePrice: 0,
  offerBoxPrice: 0,
  offerPiecePrice: 0,
  salesPrice: 0,
  offerPrice: 0,
  resolvedOfferOrSales: 0,
  resolvedPrice: 0,
  currentPrice: 0,
  typeCurrentPrice: 0,
  typeSalesPrice: 0,
  listOriginalPrice: 0,
  displayOriginalPrice: 0,
  actualPrice: 0,
  vatRate: 0,
  directVatRate: 0,
  lineVat: 0,
  discount: 0,
  overrideDiscount: null,
  productWiseDiscountValue: null,
  itemDiscount: null,
  discountRaw: null,
  discountType: "amount",
  discountTypeRaw: "",
  overrideDiscountType: "",
  productWiseDiscountType: "",
  isFixedDiscount: true,
  promoType: "",
  isPromoTypeBuy: false,
  isGetPromoType: false,
  isPromoTypeFree: false,
  isPromoted: false,
  isGenerated: false,
  promotionApplied: false,
  promotionExplicitlyRemoved: false,
  hasValidPromotionId: false,
  promotionId: "",
  isOfferDisplay: false,
  isFree: false,
  isDiscounted: false,
  isGetPromotion: false,
  isPromotedDiscounted: false,
  isRefund: false,
  isContractPrice: false,
  isItemPromoted: false,
  discountPercentage: 0,
  promoDiscPercentage: 0,
  promoDiscAmount: 0,
  promoDiscVat: 0,
  hasPromoDiscountAmount: false,
  lineSubtotal: NaN,
  backendVat: NaN,
  baseUnitPrice: NaN,
});

const parseFiniteNumber = (value, fallback = 0) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
};

const parseFiniteOrNaN = (value) => {
  if (value === undefined || value === null || value === "") return NaN;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : NaN;
};

const parseFiniteOrNull = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const parseQtyValue = (allowDecimal, raw) => {
  if (allowDecimal) return parseFloat(raw) || 0;
  return parseInt(raw, 10) || 0;
};

const toDiscountTypeKey = (value) => {
  const normalized = String(value ?? "amount").trim().toLowerCase();
  if (normalized === "fixed" || normalized === "£" || normalized === "amount") {
    return "amount";
  }
  return "percentage";
};

const readPromoType = (item) => {
  let t = String(item?.promo_type ?? item?.promoType ?? item?.promosType ?? "").trim().toUpperCase();
  if (!t && item?.buyOrGet) {
    const b = String(item.buyOrGet).trim().toUpperCase();
    if (b === "BUY" || b === "GET" || b === "FREE") t = b;
  }
  return t;
};

const readIsPromotedFlag = (item) =>
  item?.is_promoted === "1" ||
  item?.is_promoted === 1 ||
  item?.is_promoted === true;

const readIsGeneratedFlag = (item) =>
  item?.generated === true ||
  item?.generated === "1" ||
  item?.generated === 1;

const buildCartItemFieldRevision = (item) => {
  const mo = item?.manualOverrides;
  return [
    item?.id,
    item?.product_id,
    item?.qty,
    item?.order_qty,
    item?.quantity,
    item?.allot_qty,
    item?.price,
    item?.unit_price,
    item?.cart_price,
    item?.original_price,
    item?.promo_unit_price,
    item?.base_unit_price,
    item?.sales_box_price,
    item?.sales_piece_price,
    item?.offer_box_price,
    item?.offer_piece_price,
    item?.discount,
    item?.discount_type,
    item?.item_discount,
    item?.discount_percentage,
    item?.productWiseDiscountValue,
    item?.productWiseDiscountType,
    mo?.discount,
    mo?.discount_type,
    mo?.price,
    mo?.is_refund,
    item?.tax_rate,
    item?.rate,
    item?.tax_rate_percent,
    item?.taxRate,
    item?.vat_rate,
    item?.vat,
    item?.tax,
    item?.line_vat,
    item?.line_tax,
    item?.line_subtotal,
    item?.subtotal,
    item?.sub_total,
    item?.promo_type,
    item?.promoType,
    item?.promosType,
    item?.buyOrGet,
    item?.is_promoted,
    item?.generated,
    item?.promotion_applied,
    item?.promotion_id,
    item?.promotionId,
    item?.promo_id,
    item?.promoId,
    item?.promosId,
    item?.order_type,
    item?.type,
    item?.is_refund,
    item?.categorie_name,
    item?.category_name,
    item?.category,
    item?.useBackendCalculations,
    item?.tax_method,
    item?.promo_disc_percentage,
    item?.promo_disc_amount,
    item?.promo_discount_amount,
  ].join("\u0001");
};

const computeCartItemNormalizedFields = (item, revision) => {
  const isMeats = isMeatsProduct(item);
  const allowDecimal = isMeats || Config.PREFIX === "atr";
  const qty = parseQtyValue(allowDecimal, item.qty ?? item.order_qty ?? item.quantity ?? 0);
  const orderQty = parseQtyValue(allowDecimal, item.order_qty ?? item.qty ?? 0);
  const allotQty = parseQtyValue(allowDecimal, item.allot_qty ?? 0);
  const orderType = item.order_type || item.type || "Box";
  const isPiece = orderType === "Piece" || orderType === "piece";
  const price = parseFiniteNumber(item.price, 0);
  const unitPrice = parseFiniteOrNaN(item.unit_price);
  const cartPrice = parseFiniteOrNaN(item.cart_price);
  const originalPriceRaw = parseFiniteOrNaN(item.original_price);
  const promoUnitPrice = parseFiniteOrNaN(item.promo_unit_price);
  const overridePrice = parseFiniteOrNaN(item.manualOverrides?.price);
  const baseUnitPrice = parseFiniteOrNaN(item.base_unit_price);
  const salesBoxPrice = parseFiniteNumber(item.sales_box_price, 0);
  const salesPiecePrice = parseFiniteNumber(item.sales_piece_price, 0);
  const offerBoxPrice = parseFiniteNumber(item.offer_box_price, 0);
  const offerPiecePrice = parseFiniteNumber(item.offer_piece_price, 0);
  const salesPrice = isPiece ? salesPiecePrice : salesBoxPrice;
  const offerPrice = isPiece ? offerPiecePrice : offerBoxPrice;
  let resolvedOfferOrSales = 0;
  if (offerPrice > 0 && salesPrice > 0) {
    resolvedOfferOrSales = Math.min(salesPrice, offerPrice);
  } else if (offerPrice > 0) {
    resolvedOfferOrSales = offerPrice;
  } else {
    resolvedOfferOrSales = Number.isFinite(salesPrice) ? salesPrice : 0;
  }
  const orderTypeStrict = item.order_type;
  const typeSalesPrice = orderTypeStrict === "Box" ? salesBoxPrice : salesPiecePrice;
  const directVatRaw = parseFloat(
    item.tax_rate ??
      item.rate ??
      item.tax_rate_percent ??
      item.taxRate ??
      item.vat_rate ??
      ""
  );
  const directVatRate = Number.isFinite(directVatRaw) && directVatRaw >= 0 ? directVatRaw : 0;
  const lineSubtotal = parseFiniteOrNaN(
    item.line_subtotal ?? item.subtotal ?? item.sub_total
  );
  const lineVat = parseFiniteOrNaN(
    item.line_vat ?? item.line_tax ?? item.vat ?? item.tax
  );
  let vatRate = 0;
  if (Number.isFinite(directVatRaw) && directVatRaw > 0.0001) {
    vatRate = directVatRaw;
  } else if (
    Number.isFinite(lineSubtotal) &&
    lineSubtotal > 0.0001 &&
    Number.isFinite(lineVat) &&
    lineVat > 0.0001
  ) {
    vatRate = (lineVat / lineSubtotal) * 100;
  } else {
    vatRate = Number.isFinite(directVatRaw) ? directVatRaw : 0;
  }
  const overrideDiscount = parseFiniteOrNull(item.manualOverrides?.discount);
  const productWiseDiscountValue = parseFiniteOrNull(item.productWiseDiscountValue);
  const itemDiscount = parseFiniteOrNull(item.item_discount);
  const discountRaw = parseFiniteOrNull(item.discount);
  const discount = overrideDiscount != null && overrideDiscount >= 0
    ? overrideDiscount
    : productWiseDiscountValue != null && productWiseDiscountValue >= 0
      ? productWiseDiscountValue
      : itemDiscount != null && itemDiscount >= 0
        ? itemDiscount
        : discountRaw != null && discountRaw >= 0
          ? discountRaw
          : 0;
  const overrideDiscountType = item.manualOverrides?.discount_type
    ? String(item.manualOverrides.discount_type)
    : "";
  const productWiseDiscountType = item.productWiseDiscountType
    ? String(item.productWiseDiscountType)
    : "";
  const discountTypeRaw = item.discount_type ? String(item.discount_type) : "";
  const discountType = toDiscountTypeKey(
    overrideDiscountType || discountTypeRaw || productWiseDiscountType || "amount"
  );
  const isFixedDiscount = discountType === "amount";
  const promoType = readPromoType(item);
  const isPromoTypeBuy = promoType === "BUY";
  const isGetPromoType = promoType === "GET";
  const isPromoTypeFree = promoType === "FREE";
  const isPromoted = readIsPromotedFlag(item);
  const isGenerated = readIsGeneratedFlag(item);
  const promotionApplied =
    item.promotion_applied === true ||
    item.promotion_applied === "true" ||
    item.promotion_applied === 1 ||
    item.promotion_applied === "1";
  const promotionExplicitlyRemoved =
    item.promotion_applied === false ||
    item.promotion_applied === "false" ||
    item.promotion_applied === 0 ||
    item.promotion_applied === "0";
  const promotionIdRaw = item.promotion_id ?? item.promotionId ?? item.promosId;
  const promotionIdNum = Number(promotionIdRaw);
  const hasValidPromotionId =
    promotionIdRaw !== undefined &&
    promotionIdRaw !== null &&
    promotionIdRaw !== "" &&
    Number.isFinite(promotionIdNum) &&
    promotionIdNum > 0;
  const isOfferDisplay = isCartLineOfferDisplayRole(item);
  const isConfirmedFreeOffer =
    promoType !== "BUY" &&
    (isGenerated || (isOfferDisplay && (promoType === "FREE" || promoType === "GET")));
  const manualOverrideDiscount = item.manualOverrides?.discount;
  const itemDiscountValue = item.discount || item.productWiseDiscountValue;
  const effectiveDiscount =
    manualOverrideDiscount !== undefined && manualOverrideDiscount !== null
      ? manualOverrideDiscount
      : itemDiscountValue;
  const hasManualDiscount =
    (manualOverrideDiscount !== undefined &&
      manualOverrideDiscount !== null &&
      parseFloat(manualOverrideDiscount) > 0) ||
    (itemDiscountValue !== undefined &&
      itemDiscountValue !== null &&
      itemDiscountValue !== "" &&
      parseFloat(itemDiscountValue) > 0);
  let hasManual100PercentDiscount = false;
  if (effectiveDiscount !== undefined && effectiveDiscount !== null && effectiveDiscount !== "") {
    const discountValue = parseFloat(effectiveDiscount);
    if (Number.isFinite(discountValue) && discountValue > 0) {
      if (isFixedDiscount) {
        const unitForDisc = Number.isFinite(unitPrice) ? unitPrice : typeSalesPrice;
        if (unitForDisc > 0) {
          hasManual100PercentDiscount = discountValue >= unitForDisc - 0.0001;
        } else if (price === 0 && discountValue > 0) {
          hasManual100PercentDiscount = true;
        }
      } else {
        hasManual100PercentDiscount = discountValue >= 100;
      }
    }
  }
  const isGetPromotion = isPromoted && isGetPromoType;
  const sellingPrice = Math.max(
    Number.isFinite(unitPrice) ? unitPrice : 0,
    Number.isFinite(price) ? price : 0,
    resolvedOfferOrSales || 0
  );
  const isPaidBuyLine = sellingPrice > 0.0001 && !isOfferDisplay;
  const hasPromotionalDiscount = isPromoted && typeSalesPrice > 0 && typeSalesPrice > price;
  const isFreePromoType = isPromoted && isPromoTypeFree;
  const isFreeGetLine = isPromoted && isGetPromoType && price <= 0.0001;
  const isFreeDueToManualDiscount = hasManualDiscount && price === 0;
  let isFree =
    !isFreeDueToManualDiscount &&
    ((isFreePromoType || isFreeGetLine) && !hasManual100PercentDiscount);
  if (isPaidBuyLine) {
    isFree = false;
  }
  const isDiscounted =
    (hasManualDiscount || (isPromoted && hasPromotionalDiscount)) &&
    typeSalesPrice > 0 &&
    typeSalesPrice > price;
  const typeCurrentPrice = resolvedOfferOrSales > 0.0001 ? resolvedOfferOrSales : price;
  let resolvedPrice = 0;
  if (isConfirmedFreeOffer) {
    const promoPrice = Number.isFinite(promoUnitPrice)
      ? promoUnitPrice
      : Number.isFinite(price)
        ? price
        : NaN;
    resolvedPrice = Number.isFinite(promoPrice) && promoPrice >= 0 ? promoPrice : 0;
  } else if (resolvedOfferOrSales > 0.0001) {
    resolvedPrice = resolvedOfferOrSales;
  } else if (item.unit_price !== undefined && item.unit_price !== null && item.unit_price !== "" && Number.isFinite(unitPrice) && unitPrice > 0) {
    resolvedPrice = unitPrice;
  } else if (item.price !== undefined && item.price !== null && item.price !== "" && Number.isFinite(price) && price > 0) {
    resolvedPrice = price;
  } else {
    resolvedPrice = typeSalesPrice > 0 ? typeSalesPrice : typeCurrentPrice;
  }
  let listOriginalPrice = 0;
  if (salesPrice > 0) {
    listOriginalPrice = salesPrice;
  } else if (Number.isFinite(unitPrice) && unitPrice > 0) {
    listOriginalPrice = unitPrice;
  } else {
    listOriginalPrice = typeCurrentPrice;
  }
  let displayOriginalPrice = 0;
  if (Number.isFinite(originalPriceRaw) && originalPriceRaw > 0) {
    displayOriginalPrice = originalPriceRaw;
  } else if (item.useBackendCalculations && Number.isFinite(baseUnitPrice) && baseUnitPrice > 0) {
    displayOriginalPrice = baseUnitPrice;
  } else {
    displayOriginalPrice = orderType === "Box" ? salesBoxPrice : salesPiecePrice;
  }
  const actualPrice =
    item.useBackendCalculations && item.unit_price !== undefined && Number.isFinite(unitPrice)
      ? unitPrice
      : price;
  const isRefund =
    item.is_refund === "1" ||
    item.is_refund === 1 ||
    item.is_refund === true ||
    item.manualOverrides?.is_refund === "1" ||
    (Number.isFinite(overridePrice) && overridePrice < 0) ||
    (Number.isFinite(price) && price < 0);
  const discountPercentage = parseFiniteNumber(item.discount_percentage, 0);
  const promoDiscPercentage = parseFiniteNumber(item.promo_disc_percentage, 0);
  const promoDiscAmount = parseFiniteNumber(
    item.promo_disc_amount || item.promo_discount_amount,
    0
  );
  const promoDiscVat = parseFiniteNumber(
    item.promo_disc_vat || item.promo_discount_vat || item.promo_vat_line,
    0
  );
  const hasPromoDiscountAmount = promoDiscPercentage > 0 || promoDiscAmount > 0;
  const isItemPromoted =
    item.is_promoted === "1" ||
    item.is_promoted === 1 ||
    !!(item.promo_id || item.promoId || item.promosId || item.promotion_id || item.promotionId);
  const backendVat = parseFiniteOrNaN(item.vat ?? item.tax);

  return {
    revision,
    qty,
    orderQty,
    allotQty,
    isMeats,
    orderType,
    isPiece,
    price,
    unitPrice,
    cartPrice,
    originalPriceRaw,
    promoUnitPrice,
    overridePrice,
    salesBoxPrice,
    salesPiecePrice,
    offerBoxPrice,
    offerPiecePrice,
    salesPrice,
    offerPrice,
    resolvedOfferOrSales,
    resolvedPrice,
    currentPrice: price,
    typeCurrentPrice,
    typeSalesPrice,
    listOriginalPrice,
    displayOriginalPrice,
    actualPrice,
    vatRate,
    directVatRate,
    lineVat,
    discount,
    overrideDiscount,
    productWiseDiscountValue,
    itemDiscount,
    discountRaw,
    discountType,
    discountTypeRaw,
    overrideDiscountType,
    productWiseDiscountType,
    isFixedDiscount,
    promoType,
    isPromoTypeBuy,
    isGetPromoType,
    isPromoTypeFree,
    isPromoted,
    isGenerated,
    promotionApplied,
    promotionExplicitlyRemoved,
    hasValidPromotionId,
    promotionId: promotionIdRaw == null ? "" : String(promotionIdRaw),
    isOfferDisplay,
    isFree,
    isDiscounted,
    isGetPromotion,
    isPromotedDiscounted: isPromoted && isDiscounted && !isFree,
    isRefund,
    isContractPrice: item.isContractPriceApplied === "Y",
    isItemPromoted,
    discountPercentage,
    promoDiscPercentage,
    promoDiscAmount,
    promoDiscVat,
    hasPromoDiscountAmount,
    lineSubtotal,
    backendVat,
    baseUnitPrice,
  };
};

export const getCartItemNormalizedFields = (item) => {
  if (!item) return EMPTY_CART_ITEM_NORM;
  const revision = buildCartItemFieldRevision(item);
  const id = String(item.id ?? item.product_id ?? "");
  const key = `${id}\u0000${revision}`;
  const cached = cartItemNormCache.get(key);
  if (cached) return cached;
  const fields = computeCartItemNormalizedFields(item, revision);
  cartItemNormCache.set(key, fields);
  if (cartItemNormCache.size > CART_ITEM_NORM_CACHE_LIMIT) {
    const firstKey = cartItemNormCache.keys().next().value;
    cartItemNormCache.delete(firstKey);
  }
  return fields;
};

export const getCartLineDiscountComment = (item) => {
  if (!item) return "";
  const n = getCartItemNormalizedFields(item);
  if (n.isOfferDisplay || n.isGenerated || n.isGetPromoType || n.isPromoTypeFree) {
    return "";
  }
  if (!(n.discount > 0.0001)) return "";
  return String(
    item?.discount_comment ??
      item?.manualOverrides?.discount_comment ??
      item?.comment ??
      ""
  ).trim();
};

export const cartLineMissingDiscountComment = (item) => {
  // Discount comment is only required for tele sales users (role 20), not EPOS.
  if (localStorage.getItem("user_role") !== "20") return false;
  if (!item) return false;
  const n = getCartItemNormalizedFields(item);
  if (n.isOfferDisplay || n.isGenerated || n.isGetPromoType || n.isPromoTypeFree) {
    return false;
  }
  if (!(n.discount > 0.0001)) return false;
  return !getCartLineDiscountComment(item);
};

export const resolveCartItemVatRate = (item) => {
  if (!item) return 0;
  return getCartItemNormalizedFields(item).vatRate;
};

export const parseQty = (item, value) => {
  if (!item) {
    const raw = value ?? 0;
    return parseInt(raw, 10) || 0;
  }
  const n = getCartItemNormalizedFields(item);
  if (value === undefined || value === null || value === item.qty) {
    return n.qty;
  }
  if (value === item.order_qty) return n.orderQty;
  if (value === item.allot_qty) return n.allotQty;
  return allowsDecimalQty(item) || n.isMeats || Config.PREFIX === "atr"
    ? parseFloat(value) || 0
    : parseInt(value, 10) || 0;
};

export const cartLineHasPromoOfferQtyControls = (item) => {
  if (!item) return false;
  return isCartLineOfferDisplayRole(item);
};

export const determineItemType = (item) => {
  const n = getCartItemNormalizedFields(item);
  return {
    isFree: n.isFree,
    isDiscounted: n.isDiscounted,
    isPromoted: n.isPromoted,
    isGetPromotion: n.isGetPromotion,
    isPromotedDiscounted: n.isPromotedDiscounted,
    salesPrice: n.typeSalesPrice,
    currentPrice: n.typeCurrentPrice,
  };
};

export const resolveOfferOrSalesUnitPrice = (item, orderTypeOverride = null) => {
  if (!item) return 0;

  const cachedOrderType = item.order_type || item.type || item.piece_type || "Box";
  if (!orderTypeOverride || orderTypeOverride === cachedOrderType) {
    return getCartItemNormalizedFields(item).resolvedOfferOrSales;
  }

  const orderType =
    orderTypeOverride ||
    item.order_type ||
    item.type ||
    item.piece_type ||
    "Box";
  const isPiece = orderType === "Piece" || orderType === "piece";

  const salesPrice = isPiece
    ? parseFloat(item.sales_piece_price || 0)
    : parseFloat(item.sales_box_price || 0);
  const offerPrice = isPiece
    ? parseFloat(item.offer_piece_price || 0)
    : parseFloat(item.offer_box_price || 0);

  if (offerPrice > 0 && salesPrice > 0) {
    return Math.min(salesPrice, offerPrice);
  }
  if (offerPrice > 0) {
    return offerPrice;
  }
  return Number.isFinite(salesPrice) ? salesPrice : 0;
};

const isConfirmedFreeOfferCartLine = (item) => {
  if (!item) return false;
  const promoTypeNorm = String(item.promo_type ?? item.promoType ?? "").trim().toUpperCase();
  if (promoTypeNorm === "BUY") return false;
  if (
    item.generated === true ||
    item.generated === "1" ||
    item.generated === 1
  ) {
    return true;
  }
  if (!isCartLineOfferDisplayRole(item)) return false;
  return promoTypeNorm === "FREE" || promoTypeNorm === "GET";
};

const isFullyDiscountedFreeOfferLine = (item) => {
  if (!isConfirmedFreeOfferCartLine(item)) return false;
  const pct = parseFloat(item.discount_percentage ?? 0);
  if (Number.isFinite(pct) && pct >= 99.999) return true;
  const actual = parseFloat(item._actual_sub_total ?? NaN);
  if (Number.isFinite(actual) && actual <= 0.0001) return true;
  const cartPrice = parseFloat(item.cart_price ?? NaN);
  return Number.isFinite(cartPrice) && cartPrice <= 0.0001;
};

export const getPriceForItem = (item) => {
  if (!item) return 0;
  return getCartItemNormalizedFields(item).resolvedPrice;
};

/** Pre-offer unit price for strikethrough display (`cart_price` from ws-cart-list). */
export const resolveCartLineStrikethroughPrice = (item) => {
  if (!item) return 0;

  const cartPrice = parseFloat(item.cart_price ?? NaN);
  if (Number.isFinite(cartPrice) && cartPrice > 0.0001) {
    return cartPrice;
  }

  const orderType = item.order_type || item.type || "Box";
  const unitRaw = item.unit_price;
  const unitPrice =
    unitRaw !== undefined && unitRaw !== null && unitRaw !== ""
      ? parseFloat(unitRaw)
      : NaN;
  const originalPrice = parseFloat(item.original_price ?? NaN);
  const listPrice =
    orderType === "Piece"
      ? parseFloat(item.sales_piece_price || 0)
      : parseFloat(item.sales_box_price || 0);

  return Math.max(
    Number.isFinite(unitPrice) ? unitPrice : 0,
    Number.isFinite(originalPrice) ? originalPrice : 0,
    Number.isFinite(listPrice) ? listPrice : 0
  );
};

export const resolveCartLineDisplayUnitPrice = (item) => {
  const unitPriceRaw = parseFloat(item.unit_price ?? NaN);
  const priceRaw = parseFloat(item.price ?? NaN);
  const promoRaw =
    item.promo_unit_price !== undefined &&
    item.promo_unit_price !== null &&
    item.promo_unit_price !== ""
      ? parseFloat(item.promo_unit_price)
      : NaN;

  if (isConfirmedFreeOfferCartLine(item)) {
    if (Number.isFinite(promoRaw) && promoRaw >= 0) {
      return promoRaw;
    }
    if (Number.isFinite(priceRaw) && priceRaw >= 0) {
      return priceRaw;
    }
    return 0;
  }

  const resolvedOfferOrSales = resolveOfferOrSalesUnitPrice(item);
  if (resolvedOfferOrSales > 0.0001) {
    return resolvedOfferOrSales;
  }

  if (item.useBackendCalculations) {
    if (Number.isFinite(promoRaw) && promoRaw > 0.0001) {
      return promoRaw;
    }
    if (Number.isFinite(unitPriceRaw) && unitPriceRaw > 0.0001) {
      return unitPriceRaw;
    }
  }

  if (Number.isFinite(priceRaw) && priceRaw > 0.0001) {
    return priceRaw;
  }

  if (Number.isFinite(unitPriceRaw) && unitPriceRaw > 0.0001) {
    return unitPriceRaw;
  }

  const fallback = getPriceForItem(item);
  return Number.isFinite(fallback) && fallback >= 0 ? fallback : 0;
};

/** Selling unit price for paid buy rows (ignores promo_unit_price 0 on paid lines). */
export const resolveCartLinePaidSellingUnitPrice = (item) => {
  if (!item) return 0;
  if (isCartLineOfferDisplayRole(item)) {
    const promo = parseFloat(item.promo_unit_price ?? NaN);
    return Number.isFinite(promo) && promo >= 0 ? promo : 0;
  }
  const orderType = item.order_type || item.type || "Box";
  const listPrice =
    orderType === "Piece"
      ? parseFloat(item.sales_piece_price || 0)
      : parseFloat(item.sales_box_price || 0);
  // Priority chain, not Math.max: unit_price/price is the real price the
  // customer is actually charged and must win even when it's LOWER than
  // listPrice (sales_box_price, the pre-markdown standard price) - taking
  // the max wrongly picked the higher standard price whenever an item had
  // a standing markdown, inflating the line total by the markdown amount.
  // listPrice is only a last-resort fallback when the real price fields
  // are genuinely missing/zero.
  const candidates = [
    parseFloat(item.promo_unit_price ?? NaN),
    parseFloat(item.unit_price ?? NaN),
    parseFloat(item.price ?? NaN),
    parseFloat(item.discount_price ?? NaN),
    listPrice,
    parseFloat(item.original_price ?? NaN),
  ];
  for (const candidate of candidates) {
    if (Number.isFinite(candidate) && candidate > 0.0001) {
      return candidate;
    }
  }
  return 0;
};

export const parseCartLineApiSubtotal = (item) => {
  if (!item) return null;
  for (const key of ["line_subtotal", "subtotal", "sub_total"]) {
    const raw = item[key];
    if (raw === undefined || raw === null || raw === "") continue;
    const n = parseFloat(raw);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/** Paid buy rows can carry line_subtotal 0 from promotion API while unit price/qty are valid. */
export const shouldRecomputePaidLineDisplayTotal = (item, lineSubtotal, qty) => {
  if (!item || isCartLineOfferDisplayRole(item)) return false;
  const sub = Number.isFinite(lineSubtotal) ? lineSubtotal : 0;
  if (sub > 0.0001) return false;
  const unitPrice = resolveCartLinePaidSellingUnitPrice(item);
  const safeQty = parseQty(item, qty);
  return unitPrice > 0.0001 && safeQty > 0;
};

const normalizeDiscountAmount = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeDiscountTypeKey = (value) => {
  const normalized = String(value ?? "amount").trim().toLowerCase();
  if (normalized === "fixed" || normalized === "£" || normalized === "amount") {
    return "amount";
  }
  return "percentage";
};

const resolveStoredItemDiscount = (item) =>
  normalizeDiscountAmount(
    item.discount ??
      item.manualOverrides?.discount ??
      item.productWiseDiscountValue ??
      item.item_discount
  );

const resolveManualItemDiscountValue = (item) => {
  if (!item) return 0;
  return normalizeDiscountAmount(
    item.manualOverrides?.discount ??
      item.discount ??
      item.productWiseDiscountValue ??
      item.item_discount
  );
};

const isActivePromotedLineForDiscountAllocation = (item) => {
  if (!item) return false;

  const hasAppliedPromotion =
    item.promotion_applied === true ||
    item.promotion_applied === "true" ||
    item.promotion_applied === 1 ||
    item.promotion_applied === "1";

  const isGeneratedPromoLine =
    item.generated === true ||
    item.generated === "true" ||
    item.generated === 1 ||
    item.generated === "1";

  if (hasAppliedPromotion || isGeneratedPromoLine) {
    return true;
  }

  if (isCartPromoFreeLine(item)) {
    return true;
  }

  const isPromoted =
    item.is_promoted === "1" ||
    item.is_promoted === 1 ||
    item.is_promoted === true;

  if (isPromoted && !!resolveCartPromoId(item)) {
    return true;
  }

  return isCartLineOfferDisplayRole(item) && !!resolveCartPromoId(item);
};

export const isFullLineItemDiscount = (discount, discountType, lineSubtotalBeforeDiscount, qty = 1) => {
  const discountValue = normalizeDiscountAmount(discount);
  if (!(discountValue > 0.0001)) return false;
  const isFixedDiscount = normalizeDiscountTypeKey(discountType) === "amount";
  if (!isFixedDiscount) {
    return discountValue >= 100 - 0.0001;
  }
  const lineTotal = Number(lineSubtotalBeforeDiscount) || 0;
  const safeQty = Number(qty) > 0 ? Number(qty) : 1;
  const totalDiscount = discountValue * safeQty;
  return lineTotal > 0.0001 && totalDiscount >= lineTotal - 0.0001;
};

const recomputeCartLineDisplayTotal = (item, safeQty, discount, discountType, isEditMode) => {
  const normalizedDiscountType = normalizeDiscountTypeKey(discountType);
  const isFixedDiscount = normalizedDiscountType === "amount";
  const discountValue = normalizeDiscountAmount(discount);

  const explicitUnit = parseFloat(item.unit_price ?? NaN);
  const explicitPrice = parseFloat(item.price ?? NaN);
  let unitPrice = 0;
  if (Number.isFinite(explicitUnit) && explicitUnit >= 0) {
    unitPrice = explicitUnit;
  } else if (Number.isFinite(explicitPrice) && explicitPrice >= 0) {
    unitPrice = explicitPrice;
  } else {
    unitPrice = resolveCartLinePaidSellingUnitPrice(item) || 0;
  }

  const lineSubtotal = unitPrice * safeQty;

  if (!(discountValue > 0.0001)) {
    return parseFloat(lineSubtotal.toFixed(2));
  }

  if (isFixedDiscount) {
    const totalDiscount = discountValue * safeQty;
    return parseFloat(Math.max(0, lineSubtotal - totalDiscount).toFixed(2));
  }

  const discountPercent = Math.min(Math.max(discountValue, 0), 100);
  return parseFloat((lineSubtotal * (1 - discountPercent / 100)).toFixed(2));
};

export const resolveCartLineDisplayTotal = (item, qty, options = {}) => {
  const promoTypeNorm = String(item.promo_type ?? item.promoType ?? "").trim().toUpperCase();
  if (promoTypeNorm === "FREE" && isCartLineOfferDisplayRole(item)) return 0;
  if (isFullyDiscountedFreeOfferLine(item)) return 0;

  const safeQty = parseQty(item, qty ?? item.qty ?? item.allot_qty);
  const finish = (subtotal) => parseFloat(Number(subtotal).toFixed(2));

  const overridePrice = parseFloat(item?.manualOverrides?.price ?? NaN);
  const itemPrice = parseFloat(item?.price ?? NaN);
  const isRefundLine =
    item?.is_refund === "1" ||
    item?.is_refund === 1 ||
    item?.is_refund === true ||
    item?.manualOverrides?.is_refund === "1" ||
    (Number.isFinite(overridePrice) && overridePrice < 0) ||
    (Number.isFinite(itemPrice) && itemPrice < 0);
  if (isRefundLine) {
    const refundUnit = Number.isFinite(overridePrice)
      ? overridePrice
      : Number.isFinite(itemPrice)
        ? itemPrice
        : 0;
    return finish(refundUnit * safeQty);
  }

  const isEditMode = options.isEditMode === true;

  const readApiSubtotalFields = (keys) => {
    for (const key of keys) {
      const raw = item[key];
      if (raw === undefined || raw === null || raw === "") continue;
      const amount = parseFloat(raw);
      if (!Number.isFinite(amount)) continue;
      return finish(amount);
    }
    return null;
  };

  const trustedBackendSubtotal = (amount) => {
    if (amount === null || amount === undefined) return null;
    if (
      amount <= 0.0001 &&
      shouldRecomputePaidLineDisplayTotal(item, amount, safeQty)
    ) {
      return null;
    }
    return amount;
  };

  if (!isEditMode) {
    const apiSubtotal = trustedBackendSubtotal(parseCartLineApiSubtotal(item));
    if (apiSubtotal !== null) {
      return finish(apiSubtotal);
    }
  }

  const discount =
    options.discount !== undefined
      ? normalizeDiscountAmount(options.discount)
      : resolveStoredItemDiscount(item);
  const discountType =
    options.discountType ??
    item.discount_type ??
    item.manualOverrides?.discount_type ??
    item.productWiseDiscountType ??
    "amount";

  if (isEditMode && !isCartLineOfferDisplayRole(item)) {
    const recomputed = recomputeCartLineDisplayTotal(
      item,
      safeQty,
      discount,
      discountType,
      true
    );
    const unitForFullDiscount =
      parseFloat(item.unit_price ?? NaN) ||
      parseFloat(item.price ?? NaN) ||
      resolveCartLinePaidSellingUnitPrice(item) ||
      0;
    const isFullDiscount = isFullLineItemDiscount(
      discount,
      discountType,
      unitForFullDiscount * safeQty,
      safeQty
    );
    if (isFullDiscount) {
      return recomputed;
    }
    const backendTotal = trustedBackendSubtotal(
      readApiSubtotalFields(["line_subtotal", "subtotal", "sub_total"])
    );
    if (backendTotal !== null) return backendTotal;
    return recomputed;
  }

  if (item.useBackendCalculations || isEditMode) {
    const backendTotal = trustedBackendSubtotal(
      readApiSubtotalFields(["line_subtotal", "subtotal", "sub_total"])
    );
    if (backendTotal !== null) return backendTotal;
  }

  const fallbackTotal = trustedBackendSubtotal(
    readApiSubtotalFields(["subtotal", "sub_total"])
  );
  if (fallbackTotal !== null) return fallbackTotal;

  const unitPrice =
    resolveCartLinePaidSellingUnitPrice(item) ||
    parseFloat(item.unit_price || item.price || 0) ||
    0;
  const updatedItem = {
    ...item,
    qty: safeQty,
    unit_price: unitPrice,
    price: unitPrice,
    discount,
    discount_type: discountType,
    useBackendCalculations: false,
    sub_total: undefined,
    subtotal: undefined,
    line_subtotal: undefined,
  };
  return finish(calculateItemSubtotal(updatedItem));
};

export const getOriginalPrice = (item) => {
  if (!item) return 0;
  return getCartItemNormalizedFields(item).listOriginalPrice;
};

export const getExVatGoodsValueForTotals = (item) => {
  const n = getCartItemNormalizedFields(item);
  const vatRate = n.vatRate;
  const lineVat = n.lineVat;
  const taxMethod = item.tax_method || "exclusive";
  const isVatInclusive = taxMethod === "inclusive";
  const isFree = n.isFree;
  const qty = n.qty;
  const hasLineDiscount = n.discount > 0.0001;

  let rawSub = resolveCartLineDisplayTotal(
    item,
    qty,
    hasLineDiscount ? { isEditMode: true } : undefined
  );
  let usedOriginalFreeFallback = false;

  if (hasLineDiscount && (!Number.isFinite(rawSub) || rawSub <= 0.0001)) {
    return 0;
  }

  if (isFree && (!Number.isFinite(rawSub) || rawSub <= 0)) {
    const orig = Number.isFinite(n.originalPriceRaw) ? n.originalPriceRaw : 0;
    if (orig > 0 && qty > 0) {
      const listTotal = orig * qty;
      if (vatRate > 0 && isVatInclusive) {
        rawSub = listTotal / (1 + vatRate / 100);
      } else {
        rawSub = listTotal;
      }
      usedOriginalFreeFallback = true;
    }
  }

  if (!Number.isFinite(rawSub) || rawSub < 0) {
    rawSub = calculateItemSubtotal(item);
  }

  if (!Number.isFinite(rawSub) || rawSub < 0) {
    return 0;
  }

  if (usedOriginalFreeFallback) {
    return Math.max(0, rawSub);
  }

  let exVat = rawSub;
  if (isVatInclusive && Number.isFinite(lineVat) && lineVat > 0) {
    exVat = rawSub - lineVat;
  }

  return Math.max(0, exVat);
};

export const calculateItemSubtotal = (item) => {
  const n = getCartItemNormalizedFields(item);
  const overridePrice = n.overridePrice;
  const itemPrice = n.price;
  const isRefundLine = n.isRefund;

  if (isRefundLine) {
    const refundUnit = Number.isFinite(overridePrice)
      ? overridePrice
      : Number.isFinite(itemPrice)
        ? itemPrice
        : 0;
    const qty = n.qty;
    return parseFloat((refundUnit * qty).toFixed(2));
  }

  if (n.isFree) return 0;

  const manualDiscountValue = resolveManualItemDiscountValue(item);
  const hasManualDiscount = manualDiscountValue > 0.0001;

  if (!hasManualDiscount && item.useBackendCalculations && item.subtotal !== undefined && item.subtotal !== null && item.subtotal !== "") {
    const backendSubtotal = parseFloat(item.subtotal);
    if (
      Number.isFinite(backendSubtotal) &&
      backendSubtotal > 0.0001 &&
      !shouldRecomputePaidLineDisplayTotal(item, backendSubtotal, item.qty)
    ) {
      return parseFloat(backendSubtotal.toFixed(2));
    }
  }

  const hasBackendSubtotal = item.sub_total !== undefined && item.sub_total !== null && item.sub_total !== "";
  if (!hasManualDiscount && hasBackendSubtotal) {
    const backendSubtotal = parseFloat(item.sub_total);
    if (
      Number.isFinite(backendSubtotal) &&
      backendSubtotal > 0.0001 &&
      !shouldRecomputePaidLineDisplayTotal(item, backendSubtotal, item.qty)
    ) {
      return parseFloat(backendSubtotal.toFixed(2));
    }
  }

  const qty = n.qty;
  const isFixedDiscount = n.isFixedDiscount;
  const discountValue = n.overrideDiscount != null
    ? n.overrideDiscount
    : n.itemDiscount != null
      ? n.itemDiscount
      : n.discountRaw != null
        ? n.discountRaw
        : n.productWiseDiscountValue != null
          ? n.productWiseDiscountValue
          : n.discount > 0
            ? n.discount
            : 0;
  const hasDiscount = Number.isFinite(discountValue) && discountValue > 0;
  
  let basePrice;
  if (hasDiscount && Number.isFinite(n.unitPrice) && n.unitPrice > 0) {
    basePrice = n.unitPrice;
  } else {
    basePrice = n.resolvedPrice;
  }
  
  if (!basePrice || basePrice < 0) {
    basePrice = 0;
  }
  
  const subtotal = basePrice * qty;
  
  if (!hasDiscount) {
    return subtotal;
  }
  
  let discountedSubtotal;
  if (isFixedDiscount) {
    discountedSubtotal = Math.max(0, subtotal - discountValue * qty);
  } else {
    const discountPercent = Math.min(Math.max(discountValue, 0), 100);
    const discountMultiplier = 1 - discountPercent / 100;
    discountedSubtotal = subtotal * discountMultiplier;
  }
  
  return parseFloat(discountedSubtotal.toFixed(2));
};

export const calculateItemVat = (item) => {
  const n = getCartItemNormalizedFields(item);
  if (n.isFree) return 0;

  if (item.useBackendCalculations) {
    const hasBackendVat = (item.vat !== undefined && item.vat !== null && item.vat !== "") || 
                          (item.tax !== undefined && item.tax !== null && item.tax !== "");
    if (hasBackendVat) {
      const backendVat = parseFloat(item.vat || item.tax || 0);
      if (Number.isFinite(backendVat) && backendVat >= 0) {
        return parseFloat(backendVat.toFixed(2));
      }
    }
  }

  const hasBackendVat = (item.vat !== undefined && item.vat !== null && item.vat !== "") || 
                        (item.tax !== undefined && item.tax !== null && item.tax !== "");
  if (hasBackendVat && item.sub_total !== undefined && item.sub_total !== null && item.sub_total !== "") {
    const backendVat = parseFloat(item.vat || item.tax || 0);
    if (Number.isFinite(backendVat) && backendVat >= 0) {
      return parseFloat(backendVat.toFixed(2));
    }
  }

  const itemSubtotal = calculateItemSubtotal(item);
  const vatRate = n.vatRate;
  
  if (vatRate > 0 && itemSubtotal > 0) {
    return parseFloat(((itemSubtotal * vatRate) / 100).toFixed(2));
  }

  const qty = n.qty;
  const vatTotal = parseFloat(item.vat || item.tax || 0);
  
  if (vatTotal > 0) {
    const originalSubtotal = (() => {
      const price = n.resolvedPrice;
      if (!price || price < 0) return 0;
      return price * qty;
    })();
    
    if (originalSubtotal > 0) {
      const vatProportion = itemSubtotal / originalSubtotal;
      return parseFloat((vatTotal * vatProportion).toFixed(2));
    }
    
    return vatTotal;
  }

  return 0;
};

export const calculateItemTotal = (item) => {
  const subtotal = calculateItemSubtotal(item);
  const vat = calculateItemVat(item);
  return parseFloat((subtotal + vat).toFixed(2));
};

export const calculateCartSubtotal = (cartItems) => {
  const subtotal = cartItems.reduce((total, item) => {
    return total + calculateItemSubtotal(item);
  }, 0);

  return parseFloat(subtotal.toFixed(2));
};

export const calculateCartVat = (cartItems) => {
  const vat = cartItems.reduce((total, item) => {
    return total + calculateItemVat(item);
  }, 0);

  return parseFloat(vat.toFixed(2));
};

export const calculateTotalBeforeDiscount = (cartItems) => {
  const subtotal = calculateCartSubtotal(cartItems);
  const vat = calculateCartVat(cartItems);
  return parseFloat((subtotal + vat).toFixed(2));
};

export const applyOrderDiscount = (subtotal, vat, discountPercent) => {
  const discountDecimal = discountPercent / 100;

  const discountedSubtotal = subtotal * (1 - discountDecimal);
  const discountedVat = vat * (1 - discountDecimal);
  const finalTotal = (subtotal + vat) * (1 - discountDecimal);

  return {
    discountedSubtotal: parseFloat(discountedSubtotal.toFixed(2)),
    discountedVat: parseFloat(discountedVat.toFixed(2)),
    finalTotal: parseFloat(finalTotal.toFixed(2)),
    discountAmount: parseFloat(((subtotal + vat) - finalTotal).toFixed(2))
  };
};

export const calculateCartTotals = (cartItems, orderDiscountValue = 0, orderDiscountType = "percentage") => {
  let boxCount = 0;
  let pieceCount = 0;
  let freeItemsCount = 0;
  let promotedItemsCount = 0;
  let discountedItemsCount = 0;
  let zeroGoods = 0;
  let stdGoods = 0;
  let promoDiscountTotal = 0;
  let lineDiscountTotal = 0;

  const itemContributions = [];

  cartItems.forEach(item => {
    const n = getCartItemNormalizedFields(item);
    const { isFree, isPromoted, isDiscounted } = n;
    const hasVat = (parseFloat(item.vat) || parseFloat(item.tax) || 0) > 0;
    const itemSubtotal = calculateItemSubtotal(item);
    const itemVat = calculateItemVat(item);
    const itemTotal = itemSubtotal + itemVat;
    const qty = n.qty;

    if (item.order_type === "Box") {
      boxCount += qty;
    } else if (item.order_type === "Piece") {
      pieceCount += qty;
    }

    if (!hasVat) {
      zeroGoods += itemSubtotal;
    } else {
      stdGoods += itemSubtotal;
    }

    const isRefundLine = n.isRefund;

    if (!isRefundLine) {
      const raw = parseFloat(
        item.manualOverrides?.discount ??
          item.line_discount ??
          item.discount_amount ??
          item.productWiseDiscountValue ??
          item.discount ??
          0
      );
      if (Number.isFinite(raw) && raw > 0) {
        if (n.discountType === "percentage") {
          const unit = Math.abs(Number.isFinite(n.unitPrice) ? n.unitPrice : n.price);
          lineDiscountTotal += ((unit * raw) / 100) * Math.abs(qty || 0);
        } else {
          lineDiscountTotal += raw * Math.abs(qty || 0);
        }
      }
    }

    if (isDiscounted) {
      const originalPrice = n.listOriginalPrice;
      const currentPrice = n.resolvedPrice;
      const salesPrice = n.salesPrice;
      const apiPrice = n.price;
      const isPromoAlreadyAppliedByAPI = item.is_promoted === "1" && salesPrice > 0 && apiPrice > 0 && apiPrice < salesPrice;
      
      if (!isPromoAlreadyAppliedByAPI) {
        promoDiscountTotal += (originalPrice - currentPrice) * qty;
      }
    }

    if (isFree) freeItemsCount++;
    if (isPromoted) promotedItemsCount++;
    if (isDiscounted) discountedItemsCount++;

    itemContributions.push({
      id: item.id || item.product_id,
      itemTotal,
      itemSubtotal,
      itemVat
    });
  });

  const subtotal = calculateCartSubtotal(cartItems);
  const vat = calculateCartVat(cartItems);

  const totalBeforeDiscount = subtotal + vat;
  const gross = totalBeforeDiscount;
  const discountInput = parseFloat(orderDiscountValue) || 0;
  const discountTypeKey = String(orderDiscountType ?? "percentage").trim().toLowerCase();
  const isPercentDiscount = discountTypeKey === "percentage";
  let orderDiscountPercent = 0;
  if (discountInput > 0) {
    if (isPercentDiscount) {
      orderDiscountPercent = discountInput;
    } else if (totalBeforeDiscount > 0) {
      orderDiscountPercent = (Math.min(discountInput, totalBeforeDiscount) / totalBeforeDiscount) * 100;
    }
  }

  const promoDiscPercent = orderDiscountPercent > 0
    ? orderDiscountPercent
    : 0;

  let finalSubtotal = subtotal;
  let finalVat = vat;
  let grandTotal = totalBeforeDiscount;
  let discountAmount = 0;
  const itemDiscounts = new Map();

  if (orderDiscountPercent > 0) {
    const totalDiscountAmount = (totalBeforeDiscount * orderDiscountPercent) / 100;

    itemContributions.forEach((contribution) => {
      const proportion = totalBeforeDiscount > 0 ? contribution.itemTotal / totalBeforeDiscount : 0;
      const itemInvoiceDiscount = totalDiscountAmount * proportion;
      itemDiscounts.set(contribution.id, parseFloat(itemInvoiceDiscount.toFixed(2)));
    });

    const discountResult = applyOrderDiscount(subtotal, vat, orderDiscountPercent);
    finalSubtotal = discountResult.discountedSubtotal;
    finalVat = discountResult.discountedVat;
    grandTotal = discountResult.finalTotal;
    discountAmount = discountResult.discountAmount;
  }

  return {
    boxCount,
    pieceCount,
    freeItemsCount,
    promotedItemsCount,
    discountedItemsCount,
    zeroGoods: parseFloat(zeroGoods.toFixed(2)),
    stdGoods: parseFloat(stdGoods.toFixed(2)),
    gross: parseFloat(gross.toFixed(2)),
    promoDiscPercent: parseFloat(promoDiscPercent.toFixed(2)),
    promoDiscountTotal: parseFloat(promoDiscountTotal.toFixed(2)),
    lineDiscountTotal: parseFloat(lineDiscountTotal.toFixed(2)),
    subtotal,
    vat,
    totalBeforeDiscount,
    orderDiscountPercent,
    discountAmount,
    finalSubtotal,
    finalVat,
    grandTotal,
    itemDiscounts
  };
};

export const getPriceToSendToAPI = (item, options = {}) => {
  const parseStored = (val) => {
    if (val === undefined || val === null || val === "") return null;
    const n = parseFloat(val);
    return Number.isFinite(n) ? n : null;
  };

  const manualOverridePrice = parseStored(item?.manualOverrides?.price);
  if (manualOverridePrice !== null && manualOverridePrice >= 0) {
    return manualOverridePrice;
  }

  const unitPrice = parseStored(item?.unit_price);
  const storedPrice = parseStored(item?.price);

  if (unitPrice !== null && unitPrice > 0) {
    return unitPrice;
  }
  if (storedPrice !== null && storedPrice > 0) {
    return storedPrice;
  }

  const offerSalesPrice = resolveOfferOrSalesUnitPrice(item, options.orderType);
  if (offerSalesPrice > 0) {
    return offerSalesPrice;
  }

  if (
    (unitPrice === 0 || storedPrice === 0) &&
    isCartLineOfferDisplayRole(item)
  ) {
    return 0;
  }

  if (unitPrice !== null && unitPrice >= 0) {
    return unitPrice;
  }
  if (storedPrice !== null && storedPrice >= 0) {
    return storedPrice;
  }

  const discountPrice = parseStored(item?.discount_price);
  if (discountPrice !== null && discountPrice > 0) {
    return discountPrice;
  }

  return 0;
};

export const buildAddToCartPayloadForTypeChange = (item, customerId, newType, qtyChange = null) => {
  const currentQty = parseQty(item, item.qty);
  const qty = qtyChange !== null ? qtyChange : currentQty;

  const rhpItem = { ...item, order_type: newType, type: newType };
  const rhpPricing = shouldUseRhpTillPriceDiscountPayload()
    ? resolveRhpAddToCartPricing(rhpItem, qty)
    : null;
  const priceToSend = rhpPricing ? rhpPricing.price : getPriceToSendToAPI(item, { orderType: newType });
  const subTotal = rhpPricing ? rhpPricing.subTotal : priceToSend * qty;

  let discountType = "percentage";
  const itemDiscountType = item.discount_type;
  const manualDiscountType = item.manualOverrides?.discount_type;

  if (rhpPricing) {
    discountType = rhpPricing.discountType;
  } else if (itemDiscountType !== undefined && itemDiscountType !== null && itemDiscountType !== "") {
    discountType = String(itemDiscountType).trim();
  } else if (manualDiscountType !== undefined && manualDiscountType !== null && manualDiscountType !== "") {
    discountType = String(manualDiscountType).trim();
  }

  if (!rhpPricing) {
    if (discountType === "£") {
      discountType = "amount";
    } else if (discountType === "percentage") {
      discountType = "percentage";
    } else if (discountType === "amount") {
      discountType = "amount";
    } else {
      discountType = "percentage";
    }
  }

  const discountValue = rhpPricing
    ? rhpPricing.discountValue
    : parseFloat(item.manualOverrides?.discount || item.discount || 0);
  const productCode = String(item.product_code || item.code || "").trim();
  const productName = String(
    item.product_name || item.name || item.desc || ""
  ).trim();

  return appendRhpPriceBrandToPayload(
    {
      id: item.id || "",
      customer_id: customerId,
      product_id: item.product_id,
      product_code: productCode,
      product_name: productName,
      qty: qty,
      price: priceToSend.toFixed(2),
      piece_type: newType,
      sub_total: subTotal.toFixed(2),
      is_contra:
        item.isContractPriceApplied === "Y" ||
        item.is_contract_price_applied === "Y" ||
        item.is_contra === "Y" ||
        item.is_contra === "1"
          ? "1"
          : "0",
      is_refund: isRhpRefundCartLine(item) || parseFloat(priceToSend) < 0 ? "1" : "0",
      promotion_id: item.promo_id || "",
      is_promoted: item.is_promoted || "0",
      discount_type: discountType,
      discount_value: discountValue.toFixed(2),
      rate: String(item.tax_rate || item.rate || "0"),
      tax_code: resolveProductTaxCode(item),
      availableBoxQty: item.availableBoxQty,
      availablePieceQty: item.availablePieceQty,
    },
    item
  );
};

export const buildAddToCartPayload = (item, customerId, qtyChange = null) => {
  const currentQty = parseQty(item, item.qty);
  let qty;
  if (qtyChange !== null && qtyChange !== undefined) {
    const raw = qtyChange;
    const rawString = typeof raw === "string" ? raw : String(raw);
    const hasDecimal =
      (typeof raw === "number" && raw % 1 !== 0) ||
      (typeof raw === "string" && String(raw).includes("."));

    const parsed = hasDecimal || isMeatsProduct(item)
      ? parseFloat(rawString)
      : parseInt(rawString, 10);

    if (Number.isFinite(parsed)) {
      qty = parsed;
    } else {
      qty = currentQty;
    }
  } else {
    qty = currentQty;
  }

  const rhpPricing = shouldUseRhpTillPriceDiscountPayload()
    ? resolveRhpAddToCartPricing(item, qty)
    : null;
  const isRefundLine =
    isRhpRefundCartLine(item) ||
    (rhpPricing && rhpPricing.price < 0) ||
    parseFloat(item?.manualOverrides?.price ?? item?.price ?? 0) < 0;
  const qtyForSubTotal =
    isRefundLine && qty === 0
      ? parseQty(item, item.qty) || 1
      : qty;
  const rhpPricingForSubTotal =
    shouldUseRhpTillPriceDiscountPayload() && qtyForSubTotal !== qty
      ? resolveRhpAddToCartPricing(item, qtyForSubTotal)
      : rhpPricing;
  const priceToSend = rhpPricingForSubTotal
    ? rhpPricingForSubTotal.price
    : getPriceToSendToAPI(item);
  const subTotal = rhpPricingForSubTotal
    ? rhpPricingForSubTotal.subTotal
    : priceToSend * qty;

  let discountType = "amount";
  const itemDiscountType = item.discount_type || item.productWiseDiscountType;
  const manualDiscountType = item.manualOverrides?.discount_type;

  if (rhpPricing) {
    discountType = rhpPricing.discountType;
  } else if (itemDiscountType !== undefined && itemDiscountType !== null && itemDiscountType !== "") {
    discountType = String(itemDiscountType).trim();
  } else if (manualDiscountType !== undefined && manualDiscountType !== null && manualDiscountType !== "") {
    discountType = String(manualDiscountType).trim();
  }

  if (!rhpPricing) {
    if (discountType === "£") {
      discountType = "amount";
    } else if (discountType === "percentage") {
      discountType = "percentage";
    } else if (discountType === "amount") {
      discountType = "amount";
    } else {
      discountType = "amount";
    }
  }

  const discountValue = rhpPricingForSubTotal
    ? rhpPricingForSubTotal.discountValue
    : parseFloat(item.manualOverrides?.discount || item.discount || 0);

  const productCode = String(item.product_code || item.code || "").trim();
  const productName = String(
    item.product_name || item.name || item.desc || ""
  ).trim();

  return appendRhpPriceBrandToPayload(
    {
      id: item.id || "",
      customer_id: customerId,
      product_id: item.product_id,
      product_code: productCode,
      product_name: productName,
      qty: qty,
      price: priceToSend.toFixed(2),
      piece_type: item.order_type || "Box",
      sub_total: subTotal.toFixed(2),
      is_contra:
        item.isContractPriceApplied === "Y" ||
        item.is_contract_price_applied === "Y" ||
        item.is_contra === "Y" ||
        item.is_contra === "1"
          ? "1"
          : "0",
      is_refund: isRefundLine ? "1" : "0",
      promotion_id: item.promo_id || "",
      is_promoted: item.is_promoted || "0",
      discount_type: discountType,
      discount_value: discountValue.toFixed(2),
      rate: String(item.tax_rate || item.rate || "0"),
      tax_code: resolveProductTaxCode(item),
      availableBoxQty: item.availableBoxQty,
      availablePieceQty: item.availablePieceQty,
    },
    item
  );
};

export const resolveOrderTypeFromProduct = (product) => {
  if (!product) return "Box";
  const splitRaw = product.split ?? product.noOfPieceInBox;
  let splitNum = NaN;
  if (splitRaw !== undefined && splitRaw !== null && String(splitRaw).trim() !== "") {
    splitNum = parseInt(String(splitRaw), 10);
  }
  if (splitNum === 1) return "Piece";
  if (splitNum === 0) return "Box";
  if (splitNum === 2) {
    return product.order_type || product.type || "Box";
  }
  return product.order_type || product.type || "Box";
};

export const resolveRhpCartLineStoredDiscount = (item) => {
  const candidates = [
    item?.manualOverrides?.discount,
    item?.line_discount,
    item?.discount_amount,
    item?.discount,
    item?.productWiseDiscountValue,
    item?.productwiseDiscountValue,
    item?.item_discount,
  ];
  for (let i = 0; i < candidates.length; i += 1) {
    const raw = candidates[i];
    if (raw === undefined || raw === null || raw === "") {
      continue;
    }
    const n = parseFloat(raw);
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return 0;
};

export const isRhpRefundCartLine = (item) => {
  if (!item) return false;
  if (
    item.is_refund === "1" ||
    item.is_refund === 1 ||
    item.is_refund === true ||
    item.manualOverrides?.is_refund === "1"
  ) {
    return true;
  }
  const overridePrice = parseFloat(item?.manualOverrides?.price ?? NaN);
  if (Number.isFinite(overridePrice) && overridePrice < 0) {
    return true;
  }
  const price = parseFloat(item?.price ?? NaN);
  return Number.isFinite(price) && price < 0;
};

export const resolveRhpCartLineListPrice = (item) => {
  const overridePrice = parseFloat(item?.manualOverrides?.price ?? NaN);
  if (Number.isFinite(overridePrice) && overridePrice !== 0) {
    return overridePrice;
  }

  const apiPrice = parseFloat(item?.price ?? NaN);
  const apiUnitPrice = parseFloat(item?.unit_price ?? NaN);
  const isRefundFlag =
    item?.is_refund === "1" ||
    item?.is_refund === 1 ||
    item?.is_refund === true ||
    item?.manualOverrides?.is_refund === "1";

  if (Number.isFinite(apiPrice) && apiPrice < 0) {
    return apiPrice;
  }
  if (Number.isFinite(apiUnitPrice) && apiUnitPrice < 0) {
    return apiUnitPrice;
  }
  if (isRefundFlag) {
    if (Number.isFinite(apiPrice) && apiPrice !== 0) {
      return apiPrice < 0 ? apiPrice : -Math.abs(apiPrice);
    }
    if (Number.isFinite(apiUnitPrice) && apiUnitPrice !== 0) {
      return apiUnitPrice < 0 ? apiUnitPrice : -Math.abs(apiUnitPrice);
    }
  }

  const orderType = item?.order_type || item?.type || "Box";
  const isPiece = orderType === "Piece" || orderType === "piece";
  const storedDiscount = resolveRhpCartLineStoredDiscount(item);
  const qty = parseQty(item, item.qty) || 1;
  const subTotal = parseFloat(item?.sub_total ?? item?.subtotal ?? NaN);

  if (isPiece) {
    const fromList = parseFloat(item?.list_piece_price ?? NaN);
    if (Number.isFinite(fromList) && fromList > 0) {
      return fromList;
    }
    const price = parseFloat(item?.split_price ?? item?.price ?? 0);
    if (Number.isFinite(price) && price > 0) {
      if (storedDiscount > 0) {
        const charged = Number.isFinite(subTotal) && subTotal > 0 ? subTotal / qty : NaN;
        if (Number.isFinite(charged) && Math.abs(price - charged) < 0.02) {
          return price + storedDiscount;
        }
      }
      return price;
    }
    return 0;
  }

  const fromList = parseFloat(item?.list_box_price ?? NaN);
  if (Number.isFinite(fromList) && fromList > 0) {
    return fromList;
  }

  const price = Number.isFinite(apiPrice) ? apiPrice : 0;
  const unitPrice = apiUnitPrice;
  if (Number.isFinite(unitPrice) && unitPrice > price && unitPrice > 0) {
    return unitPrice;
  }
  if (Number.isFinite(price) && price > 0) {
    if (storedDiscount > 0) {
      const charged = Number.isFinite(subTotal) && subTotal > 0 ? subTotal / qty : NaN;
      if (Number.isFinite(charged) && Math.abs(price - charged) < 0.02) {
        return price + storedDiscount;
      }
      const band = parseFloat(item?.band_box_price ?? item?.final_price ?? NaN);
      if (Number.isFinite(band) && band > 0 && price > band) {
        return price;
      }
    }
    return price;
  }
  return 0;
};

export const resolveRhpCartLineBandPrice = (item) => {
  const orderType = item?.order_type || item?.type || "Box";
  const isPiece = orderType === "Piece" || orderType === "piece";
  if (isPiece) {
    const n = parseFloat(
      item?.band_piece_price ?? item?.final_split_price ?? 0
    );
    return Number.isFinite(n) ? n : 0;
  }
  const n = parseFloat(item?.band_box_price ?? item?.final_price ?? 0);
  return Number.isFinite(n) ? n : 0;
};

export const resolveRhpPricingFromProduct = (product, orderType = "Box") => {
  const isPiece = orderType === "Piece" || orderType === "piece";
  const listPrice = isPiece
    ? parseFloat(product?.split_price ?? product?.list_piece_price ?? 0)
    : parseFloat(product?.price ?? product?.list_box_price ?? 0);
  const bandPrice = isPiece
    ? parseFloat(product?.final_split_price ?? product?.band_piece_price ?? 0)
    : parseFloat(product?.final_price ?? product?.band_box_price ?? 0);
  const safeList = Number.isFinite(listPrice) ? listPrice : 0;
  const safeBand = Number.isFinite(bandPrice) ? bandPrice : 0;
  const discountAmount =
    safeBand > 0 && safeList > safeBand ? safeList - safeBand : 0;
  return {
    listPrice: safeList,
    bandPrice: safeBand,
    discountAmount,
  };
};

export const buildRhpCartPriceFields = (product, orderType = "Box") => {
  const { listPrice, bandPrice, discountAmount } = resolveRhpPricingFromProduct(
    product,
    orderType
  );
  return {
    list_box_price: String(product?.price ?? listPrice ?? "0"),
    list_piece_price: String(product?.split_price ?? listPrice ?? "0"),
    band_box_price: String(product?.final_price ?? bandPrice ?? "0"),
    band_piece_price: String(product?.final_split_price ?? bandPrice ?? "0"),
    final_price: product?.final_price ?? "",
    final_split_price: product?.final_split_price ?? "",
    split_price: product?.split_price ?? "",
    price: listPrice.toFixed(2),
    unit_price: listPrice.toFixed(2),
    discount: discountAmount.toFixed(2),
    discount_type: "amount",
    productWiseDiscountValue: discountAmount.toFixed(2),
    productWiseDiscountType: "amount",
  };
};

export const resolveRhpCartLinePriceDiscount = (item) => {
  const listPrice = resolveRhpCartLineListPrice(item);
  const bandPrice = resolveRhpCartLineBandPrice(item);
  if (bandPrice > 0 && listPrice > bandPrice) {
    return listPrice - bandPrice;
  }
  return resolveRhpCartLineStoredDiscount(item);
};

const appendRhpPriceBrandToPayload = (payload, item) => {
  if (!isRhpEposTenant()) {
    return payload;
  }
  const brand = item?.price_brand ?? item?.priceBrand;
  if (brand !== undefined && brand !== null && String(brand).trim() !== "") {
    payload.price_brand = String(brand).trim();
  }
  return payload;
};

export const isRhpEposTenant = () => Config.PREFIX === "rhp";

export const shouldUseRhpTillPriceDiscountPayload = () => {
  if (!isRhpEposTenant()) {
    return false;
  }
  try {
    const role =
      localStorage.getItem("active_role_id") ||
      localStorage.getItem("user_role");
    return String(role) === "18";
  } catch {
    return false;
  }
};

export const resolveRhpAddToCartPricing = (item, qty) => {
  const listPrice = resolveRhpCartLineListPrice(item);
  const isRefund = isRhpRefundCartLine(item) || listPrice < 0;
  const applyTillDiscount = shouldUseRhpTillPriceDiscountPayload();
  const discountAmount =
    isRefund || !applyTillDiscount ? 0 : resolveRhpCartLinePriceDiscount(item);
  const unitCharge = isRefund
    ? listPrice
    : applyTillDiscount
      ? Math.max(0, listPrice - discountAmount)
      : getPriceToSendToAPI(item);
  const safeQty = Number.isFinite(qty) && qty > 0 ? qty : 0;
  const priceForPayload = applyTillDiscount || isRefund ? listPrice : unitCharge;
  return {
    price: priceForPayload,
    discountType: "amount",
    discountValue: discountAmount,
    subTotal: (isRefund ? listPrice : unitCharge) * safeQty,
  };
};

export const prepareProductForCart = (product) => {
  if (!product) return product;

  const orderType = resolveOrderTypeFromProduct(product);
  const isPiece = orderType === "Piece" || orderType === "piece";

  const salesPrice = isPiece
    ? parseFloat(product.final_split_price || product.piece_price1 || product.split_price || 0)
    : parseFloat(product.final_price || product.price_1 || product.price || 0);
  const offerPrice = isPiece
    ? parseFloat(product.discountedSplitPrice || product.piece_offer_price || 0)
    : parseFloat(product.discountedBoxPrice || product.box_offer_price || 0);

  let priceToUse;
  if (offerPrice > 0 && salesPrice > 0) {
    priceToUse = Math.min(salesPrice, offerPrice);
  } else if (offerPrice > 0) {
    priceToUse = offerPrice;
  } else {
    priceToUse = salesPrice;
  }

  const standardBoxPrice = product.standard_box_price ?? product.price ?? 0;
  const standardPiecePrice = product.standard_piece_price ?? product.split_price ?? 0;

  const prepared = {
    ...product,
    ...extractMaximumOrderQuantityFields(product),
    order_type: orderType,
    type: orderType,
    unit_price: priceToUse > 0 ? priceToUse : (product.unit_price || 0),
    standard_box_price: standardBoxPrice,
    standard_piece_price: standardPiecePrice,
  };

  if (shouldUseRhpTillPriceDiscountPayload()) {
    Object.assign(prepared, buildRhpCartPriceFields(product, orderType));
  }

  return prepared;
};

export const transformProductToCartItem = (product, customerId = null) => {
  let orderType = product.order_type;

  if (!orderType && customerId) {
    const storedOrderType = productSplitService.getDefaultOrderType(customerId, product.id);
    if (storedOrderType) {
      orderType = storedOrderType;
    }
  }

  if (!orderType) {
    orderType = product.split === "1" ? "Piece" : "Box";
  }

  let price;
  if (product.unit_price !== undefined && product.unit_price !== null && parseFloat(product.unit_price) > 0) {
    price = parseFloat(product.unit_price);
  } else {
    const isPiece = orderType === "Piece" || orderType === "piece";
    const salesPrice = isPiece
      ? parseFloat(product.final_split_price || product.piece_price1 || product.split_price || 0)
      : parseFloat(product.final_price || product.price_1 || product.price || 0);
    const offerPrice = isPiece
      ? parseFloat(product.discountedSplitPrice || product.piece_offer_price || 0)
      : parseFloat(product.discountedBoxPrice || product.box_offer_price || 0);

    if (offerPrice > 0 && salesPrice > 0) {
      price = Math.min(salesPrice, offerPrice);
    } else if (offerPrice > 0) {
      price = offerPrice;
    } else {
      price = salesPrice;
    }
  }

  const qty = product.qty !== undefined && product.qty !== null && product.qty !== ""
    ? String(product.qty)
    : (product.quantity !== undefined && product.quantity !== null && product.quantity !== ""
      ? String(product.quantity)
      : "1");
  const qtyNum = isMeatsProduct(product) ? parseFloat(qty) || 1 : parseInt(qty, 10) || 1;
  const subTotal = (price * qtyNum).toFixed(2);

  const productCode = String(product.product_code || product.code || "").trim();
  const productName = String(product.product_name || product.name || "").trim();

  let cartPrice = price;
  let cartUnitPrice = product.unit_price || price.toFixed(2);
  let cartDiscount = "0.00";
  let cartDiscountType = "amount";
  let cartSubTotal = subTotal;
  let rhpPriceFields = {};

  if (shouldUseRhpTillPriceDiscountPayload()) {
    rhpPriceFields = buildRhpCartPriceFields(product, orderType);
    const rhpPricing = resolveRhpAddToCartPricing(
      { ...product, ...rhpPriceFields, order_type: orderType, type: orderType },
      qtyNum
    );
    cartPrice = rhpPricing.price;
    cartUnitPrice = rhpPricing.price.toFixed(2);
    cartDiscount = rhpPricing.discountValue.toFixed(2);
    cartDiscountType = rhpPricing.discountType;
    cartSubTotal = rhpPricing.subTotal.toFixed(2);
  }

  return {
    id: "",
    product_id: product.id || product.product_id,
    code: productCode,
    product_code: productCode,
    name: productName,
    product_name: productName,
    desc: productName,
    price: cartPrice.toFixed(2),
    unit_price: cartUnitPrice,
    discount: cartDiscount,
    discount_type: cartDiscountType,
    productWiseDiscountValue: cartDiscount,
    productWiseDiscountType: cartDiscountType,
    sales_box_price: product.price ?? "0",
    sales_piece_price: product.split_price ?? "0",
    list_box_price: rhpPriceFields.list_box_price ?? product.price ?? "0",
    list_piece_price: rhpPriceFields.list_piece_price ?? product.split_price ?? "0",
    band_box_price: rhpPriceFields.band_box_price ?? product.final_price ?? "0",
    band_piece_price: rhpPriceFields.band_piece_price ?? product.final_split_price ?? "0",
    final_price: product.final_price ?? "",
    final_split_price: product.final_split_price ?? "",
    split_price: product.split_price ?? "",
    price_brand: product.price_brand ?? product.priceBrand ?? "",
    offer_box_price: product.discountedBoxPrice || "0",
    offer_piece_price: product.discountedSplitPrice || "0",
    vat: "0",
    tax: "0",
    qty: qty,
    sub_total: cartSubTotal,
    order_type: orderType,
    is_promoted: product.is_promoted || "0",
    promo_id: product.promo_id || "",
    promo_type: "",
    isContractPriceApplied: product.isContractPriceApplied || "N",
    image: product.image || "",
    size: product.size || "",
    split: product.split || "0",
    tax_rate: product.tax_rate || product.rate || "0",
    tax_code: resolveProductTaxCode(product),
    noOfPieceInBox: product.noOfPieceInBox || "1",
    cost: product.cost || "0",
    categorie_name: product.categorie_name || product.category_name || product.category || "",
    availableBoxQty: product.availableBoxQty !== undefined && product.availableBoxQty !== null ? Number(product.availableBoxQty) : 0,
    availablePieceQty: product.availablePieceQty !== undefined && product.availablePieceQty !== null ? Number(product.availablePieceQty) : 0,
    ...extractMaximumOrderQuantityFields(product),
  };
};

const normalizePromoIdForDedupeKey = (item) => {
  const raw = item?.promo_id ?? item?.promotion_id ?? item?.promosId ?? item?.promoId;
  if (raw === null || raw === undefined || raw === "") return "";
  const s = String(raw).trim();
  if (!s || s.toLowerCase() === "null" || s === "0") return "";
  return s;
};


const normalizedPromoTypeForDedupe = (item) =>
  getCartItemNormalizedFields(item).promoType;

const promotionNameForLabelKey = (item) =>
  String(item?.promotion_name ?? item?.promotionName ?? item?.promo_name ?? "")
    .trim()
    .toLowerCase();

/** Group cart lines that belong to the same promotion (prefer shared name over id). */
const promoGroupKeyForItem = (item) => {
  const name = promotionNameForLabelKey(item);
  if (name) return `name:${name}`;
  const pid = normalizePromoIdForDedupeKey(item);
  if (pid) return `id:${pid}`;
  return "";
};

const inferBuyQuantityFromPromotionName = (item) => {
  const name = String(
    item.promotion_name ?? item.promotionName ?? item.promo_name ?? ""
  ).trim();
  let match = name.match(/\bBUY\s+(?:ANY\s+)?(\d+)\b/i);
  if (match) {
    const n = parseInt(match[1], 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
  match = name.match(/\b(\d+)\s+FOR\s+(?:£|\$|€)?/i);
  if (match) {
    const n = parseInt(match[1], 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }
  return 0;
};

const parseBundleSetsCount = (bundle) => {
  for (const key of ["complete_sets", "sets_count", "get_sets_count"]) {
    const sets = parseInt(bundle?.[key], 10);
    if (Number.isFinite(sets) && sets > 0) return sets;
  }
  return 0;
};

const parseBundleBuyQuantityPerSet = (bundle) => {
  for (const key of [
    "buy_quantity_per_set",
    "buy_quantity",
    "buyQuantity",
    "buy_qty_per_set",
  ]) {
    const qty = parseInt(bundle?.[key], 10);
    if (Number.isFinite(qty) && qty > 0) return qty;
  }
  return 0;
};

const resolveFixedSetOfferPriceForLines = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return 0;

  for (const line of lines) {
    for (const key of [
      "set_offer_price",
      "setOfferPrice",
      "promo_set_price",
      "promotion_set_price",
      "bundle_price",
    ]) {
      const price = parseCartBundleAmount(line?.[key]);
      if (price > 0.0001) return price;
    }
  }

  for (const line of lines) {
    const name = promotionNameForLabelKey(line);
    const match = name.match(/\b\d+\s+FOR\s+(?:£|\$|€)?\s*(\d+(?:\.\d+)?)/i);
    if (match) {
      const price = parseFloat(match[1]);
      if (Number.isFinite(price) && price > 0.0001) return price;
    }
  }

  return 0;
};

const lineQtyForPromoLabel = (item) => {
  const fields = ["allot_qty", "accept_qty", "qty", "order_qty", "quantity"];
  for (const field of fields) {
    const raw = item?.[field];
    if (raw === undefined || raw === null || String(raw).trim() === "") continue;
    const n = parseInt(String(raw), 10);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return 0;
};

const resolveBuyQuantityForPromo = (items, groupKey, bundleDiscounts = []) => {
  if (Array.isArray(bundleDiscounts) && bundleDiscounts.length > 0) {
    for (const item of items) {
      if (promoGroupKeyForItem(item) !== groupKey) continue;
      const promoId = resolveCartPromoId(item);
      if (!promoId) continue;
      for (const bundle of bundleDiscounts) {
        if (String(bundle?.promotion_id ?? "").trim() !== promoId) continue;
        const fromBundle = parseBundleBuyQuantityPerSet(bundle);
        if (fromBundle > 0) return fromBundle;
      }
    }
  }

  for (const item of items) {
    if (promoGroupKeyForItem(item) !== groupKey) continue;
    const fields = [
      item.buy_quantity,
      item.buyQuantity,
      item.promotion_buy_quantity,
      item.promotionBuyQuantity,
    ];
    for (const raw of fields) {
      const bq = parseInt(raw ?? 0, 10);
      if (bq > 0) return bq;
    }
    const inferred = inferBuyQuantityFromPromotionName(item);
    if (inferred > 0) return inferred;
  }
  return 0;
};

export const resolveBuyQuantityForCartPromoGroup = (item, cartItems, bundleDiscounts = []) => {
  const groupKey = promoGroupKeyForItem(item);
  if (!groupKey || !Array.isArray(cartItems)) return 0;
  return resolveBuyQuantityForPromo(cartItems, groupKey, bundleDiscounts);
};

const buyPoolProductIdentity = (item) => {
  const productId = String(item?.product_id ?? item?.productId ?? "").trim();
  if (productId) return `p:${productId}`;
  const code = String(item?.code ?? item?.product_code ?? "").trim().toLowerCase();
  if (code) return `c:${code}`;
  return "";
};

const countDistinctBuyPoolProducts = (itemList) => {
  const keys = new Set();
  if (!Array.isArray(itemList)) return 0;
  for (let i = 0; i < itemList.length; i += 1) {
    const it = itemList[i];
    if (!it) continue;
    const k = buyPoolProductIdentity(it);
    keys.add(k || `i:${i}`);
  }
  return keys.size;
};

const isPromotedBuyPoolLine = (item) => {
  const promoType = normalizedPromoTypeForDedupe(item);
  if (promoType === "GET" || promoType === "FREE") return false;
  if (promoType === "BUY") return lineQtyForPromoLabel(item) > 0;
  const groupKey = promoGroupKeyForItem(item);
  if (!groupKey) return false;
  const promoted =
    item.is_promoted === "1" ||
    item.is_promoted === 1 ||
    item.is_promoted === true ||
    item.promotion_applied === true ||
    item.promotion_applied === "true" ||
    item.promotion_applied === 1 ||
    item.promotion_applied === "1";
  return promoted && lineQtyForPromoLabel(item) > 0;
};

/**
 * Promotion name label rules (Buy X / Buy X Get Y):
 * - GET/FREE: label count follows promotion sets, capped by offer lines.
 * - BUY (pooled across products): label count = floor(totalBuyQty / buyQuantity).
 *   e.g. Buy 4 with qty 2+1+1 → 1 label on last row; Buy 2 with qty 2+2 → 2 labels.
 */
export const applyShowPromoLabelDedupe = (items, bundleDiscounts = []) => {
  if (!Array.isArray(items) || items.length === 0) return items;

  const buyLinesByPromo = new Map();
  const getFreeIndexesByPromo = new Map();

  items.forEach((item, index) => {
    const groupKey = promoGroupKeyForItem(item);
    if (!groupKey) return;
    const promoType = normalizedPromoTypeForDedupe(item);
    if (promoType === "GET" || promoType === "FREE") {
      if (!getFreeIndexesByPromo.has(groupKey)) {
        getFreeIndexesByPromo.set(groupKey, []);
      }
      getFreeIndexesByPromo.get(groupKey).push(index);
      return;
    }
    if (isPromotedBuyPoolLine(item)) {
      if (!buyLinesByPromo.has(groupKey)) {
        buyLinesByPromo.set(groupKey, []);
      }
      buyLinesByPromo.get(groupKey).push({ index, qty: lineQtyForPromoLabel(item) });
    }
  });

  const showPromoIndexes = new Set();
  const showPromoSetsByIndex = new Map();
  const promoGroupKeys = new Set([
    ...getFreeIndexesByPromo.keys(),
    ...buyLinesByPromo.keys(),
  ]);

  const assignLabelsForSets = (lines, sets, groupKey) => {
    if (sets <= 0 || lines.length === 0) return;
    const buyQuantity = resolveBuyQuantityForPromo(items, groupKey, bundleDiscounts);
    const qualifying =
      buyQuantity > 0 ? lines.filter((line) => line.qty >= buyQuantity) : [];
    if (qualifying.length > 0 && qualifying.length >= sets) {
      qualifying.slice(-sets).forEach((line) => {
        showPromoIndexes.add(line.index);
        showPromoSetsByIndex.set(line.index, (showPromoSetsByIndex.get(line.index) || 0) + 1);
      });
      return;
    }
    lines.slice(-sets).forEach((line) => {
      showPromoIndexes.add(line.index);
      showPromoSetsByIndex.set(line.index, (showPromoSetsByIndex.get(line.index) || 0) + 1);
    });
  };

  const resolveSetsForPromoGroup = (groupKey, buyLines, buyQuantity) => {
    for (const item of items) {
      if (promoGroupKeyForItem(item) !== groupKey) continue;
      const promoId = resolveCartPromoId(item);
      if (!promoId || !Array.isArray(bundleDiscounts)) break;
      for (const bundle of bundleDiscounts) {
        if (String(bundle?.promotion_id ?? "").trim() !== promoId) continue;
        const sets = parseBundleSetsCount(bundle);
        if (sets > 0) return sets;
      }
    }

    const totalBuyQty = buyLines.reduce((sum, line) => sum + line.qty, 0);
    if (buyQuantity > 0 && totalBuyQty > 0) {
      const inferredSets = Math.floor(totalBuyQty / buyQuantity);
      if (inferredSets > 0) return inferredSets;
    }

    return totalBuyQty > 0 ? 1 : 0;
  };

  const assignBuyPoolPromoLabelsPerSet = (buyLines, sets, groupKey) => {
    if (sets <= 0 || buyLines.length === 0) return;

    const buyQuantity = resolveBuyQuantityForPromo(items, groupKey, bundleDiscounts);
    if (buyQuantity <= 0) {
      assignLabelsForSets(buyLines, sets, groupKey);
      return;
    }

    const uniqueProductCount = countDistinctBuyPoolProducts(
      buyLines.map((line) => items[line.index])
    );
    if (uniqueProductCount <= 1) {
      const last = [...buyLines].sort((a, b) => a.index - b.index)[buyLines.length - 1];
      showPromoIndexes.add(last.index);
      showPromoSetsByIndex.set(last.index, 1);
      return;
    }

    const sorted = [...buyLines].sort((a, b) => a.index - b.index);
    let labelsNeeded = sets;
    let runningQty = 0;
    let completedSets = 0;

    for (const line of sorted) {
      if (labelsNeeded <= 0) break;
      runningQty += line.qty;
      const nowCompleted = Math.min(Math.floor(runningQty / buyQuantity), sets);
      const newSets = nowCompleted - completedSets;
      if (newSets <= 0) continue;
      showPromoIndexes.add(line.index);
      showPromoSetsByIndex.set(
        line.index,
        (showPromoSetsByIndex.get(line.index) || 0) + newSets
      );
      labelsNeeded -= newSets;
      completedSets = nowCompleted;
    }
  };

  promoGroupKeys.forEach((groupKey) => {
    const getIndexes = getFreeIndexesByPromo.get(groupKey) || [];
    const buyLines = buyLinesByPromo.get(groupKey) || [];

    const buyQuantity = resolveBuyQuantityForPromo(items, groupKey, bundleDiscounts);
    const setsFromBuy = resolveSetsForPromoGroup(groupKey, buyLines, buyQuantity);

    if (getIndexes.length > 0) {
      const getLines = getIndexes
        .filter((idx) => lineQtyForPromoLabel(items[idx]) > 0)
        .map((index) => ({
          index,
          qty: lineQtyForPromoLabel(items[index]),
        }));
      const labelCount =
        setsFromBuy > 0 ? setsFromBuy : getLines.length > 0 ? 1 : 0;
      if (buyLines.length > 0 && setsFromBuy > 0) {
        assignBuyPoolPromoLabelsPerSet(buyLines, setsFromBuy, groupKey);
        return;
      }
      assignLabelsForSets(
        getLines,
        Math.min(labelCount, getLines.length),
        groupKey
      );
      return;
    }

    if (buyLines.length === 0 || setsFromBuy <= 0) return;
    assignBuyPoolPromoLabelsPerSet(buyLines, setsFromBuy, groupKey);
  });

  const promotionNameByGroup = new Map();
  items.forEach((item) => {
    if (!item) return;
    const groupKey = promoGroupKeyForItem(item);
    if (!groupKey) return;
    const name = String(
      item.promotion_name ?? item.promotionName ?? item.promo_name ?? ""
    ).trim();
    if (name && !promotionNameByGroup.has(groupKey)) {
      promotionNameByGroup.set(groupKey, name);
    }
  });

  return items.map((item, index) => {
    if (!item) return item;
    const groupKey = promoGroupKeyForItem(item);
    const sharedName = groupKey ? promotionNameByGroup.get(groupKey) : "";
    const hasName = String(
      item.promotion_name ?? item.promotionName ?? item.promo_name ?? ""
    ).trim();
    const showPromo = showPromoIndexes.has(index);
    const promoDisplaySets = showPromo ? showPromoSetsByIndex.get(index) || 0 : 0;
    const nextPromotionName =
      showPromo && sharedName && !hasName ? sharedName : undefined;
    const prevShowPromo = Boolean(item.showPromo);
    const prevPromoDisplaySets = Number(item.promoDisplaySets) || 0;
    const prevPromotionName = String(
      item.promotion_name ?? item.promotionName ?? item.promo_name ?? ""
    ).trim();
    if (
      prevShowPromo === showPromo &&
      prevPromoDisplaySets === promoDisplaySets &&
      (!nextPromotionName || prevPromotionName === nextPromotionName)
    ) {
      return item;
    }
    return {
      ...item,
      showPromo,
      promoDisplaySets,
      ...(nextPromotionName
        ? { promotion_name: nextPromotionName, promotionName: nextPromotionName, promo_name: nextPromotionName }
        : {}),
    };
  });
};

const roundCartAmount = (value) => parseFloat((Number(value) || 0).toFixed(2));

const parseCartBundleAmount = (value) => {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : 0;
};

const isCartPromoFreeLine = (item) => {
  const promoTypeNorm = String(item?.promo_type || item?.promoType || "")
    .trim()
    .toUpperCase();
  if (promoTypeNorm === "FREE") return true;
  if (promoTypeNorm === "GET" && isCartLineOfferDisplayRole(item)) return true;
  return isFullyDiscountedFreeOfferLine(item);
};

const resolveBundlePromoNetExcludingGet = (bundle) => {
  const buyPart = parseCartBundleAmount(bundle?.buy_total_discount);
  const getPart = parseCartBundleAmount(bundle?.get_total_discount);
  const directTotal = parseCartBundleAmount(
    bundle?.total_promo_discount ?? bundle?.total_discount
  );
  if (buyPart > 0.0001) return buyPart;
  if (directTotal > 0.0001) return Math.max(0, directTotal - getPart);
  return 0;
};

const resolveBundlePromoVatExcludingGet = (bundle) => {
  const buyVat = parseCartBundleAmount(bundle?.buy_discount_vat);
  const getVat = parseCartBundleAmount(bundle?.get_discount_vat);
  const totalVat = parseCartBundleAmount(
    bundle?.total_promo_discount_vat ?? bundle?.promo_discount_vat
  );
  if (buyVat > 0.0001) return buyVat;
  if (totalVat > 0.0001) return Math.max(0, totalVat - getVat);
  return 0;
};

const resolveCartPromoId = (item) => {
  const raw = item?.promo_id ?? item?.promotion_id ?? item?.promosId ?? item?.promoId;
  if (raw === null || raw === undefined || raw === "") return "";
  return String(raw).trim();
};

export const buildVatBreakdownFromCartItems = (cartItems) => {
  const vatBreakdown = {};
  if (!Array.isArray(cartItems)) return vatBreakdown;

  cartItems.forEach((item) => {
    if (isCartPromoFreeLine(item)) return;

    const exVat = getExVatGoodsValueForTotals(item);
    if (exVat <= 0.0001) return;

    const taxCode = String(resolveProductTaxCode(item) || "0");
    const vatRate = resolveCartItemVatRate(item);

    if (!vatBreakdown[taxCode]) {
      vatBreakdown[taxCode] = { rate: vatRate, net: 0 };
    }
    vatBreakdown[taxCode].net += exVat;
    if (vatRate > 0 && (!vatBreakdown[taxCode].rate || vatBreakdown[taxCode].rate <= 0)) {
      vatBreakdown[taxCode].rate = vatRate;
    }
  });

  return vatBreakdown;
};

export const buildBundleAllocationByTaxCodeForCart = (
  bundleDiscounts,
  cartItems,
  vatBreakdown
) => {
  const bundleNetByCode = {};

  const addBundleToCode = (code, net) => {
    if (code === undefined || code === null || String(code).trim() === "") return;
    const c = String(code);
    bundleNetByCode[c] = (bundleNetByCode[c] || 0) + net;
  };

  const bundleNetPart = (item) => resolveBundlePromoNetExcludingGet(item);

  const bundleVatPart = (item) => {
    const tpv = parseCartBundleAmount(item.total_promo_discount_vat);
    if (tpv > 0.0001) return tpv;
    return (
      parseCartBundleAmount(item.buy_discount_vat) +
      parseCartBundleAmount(item.get_discount_vat) +
      parseCartBundleAmount(item.promo_discount_vat) +
      parseCartBundleAmount(item.discount_vat)
    );
  };

  const inferTaxCodeFromBundle = (net, vat) => {
    if (net <= 0.0001) return null;
    if (vat <= 0.0001) {
      for (const code of Object.keys(vatBreakdown)) {
        if ((parseFloat(vatBreakdown[code].rate) || 0) <= 0.0001) {
          return code;
        }
      }
    }
    const impliedRate = (vat / net) * 100;
    let bestCode = null;
    let bestDiff = Infinity;
    Object.keys(vatBreakdown).forEach((code) => {
      const r = parseFloat(vatBreakdown[code].rate) || 0;
      const diff = Math.abs(r - impliedRate);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestCode = code;
      }
    });
    return bestCode;
  };

  const taxCodeWeightsForPromo = (promoId) => {
    const weights = new Map();
    if (!Array.isArray(cartItems)) return weights;

    cartItems.forEach((item) => {
      const pid = resolveCartPromoId(item);
      if (!pid || pid !== String(promoId).trim()) return;

      const code = String(resolveProductTaxCode(item) || "0");
      const lineNet = getExVatGoodsValueForTotals(item);
      if (lineNet <= 0.0001) return;
      weights.set(code, (weights.get(code) || 0) + lineNet);
    });
    return weights;
  };

  if (!Array.isArray(bundleDiscounts)) return bundleNetByCode;

  bundleDiscounts.forEach((item) => {
    const net = bundleNetPart(item);
    if (net <= 0.0001) return;
    const promoId = String(item.promotion_id ?? "").trim();
    const codeWeights = taxCodeWeightsForPromo(promoId);

    if (codeWeights.size === 1) {
      addBundleToCode([...codeWeights.keys()][0], net);
    } else if (codeWeights.size > 1) {
      const totalWeight = [...codeWeights.values()].reduce((sum, w) => sum + w, 0);
      codeWeights.forEach((weight, code) => {
        addBundleToCode(code, net * (weight / totalWeight));
      });
    } else {
      addBundleToCode(inferTaxCodeFromBundle(net, bundleVatPart(item)), net);
    }
  });

  return bundleNetByCode;
};

/** Backend `promotion_discount` per line, grouped by tax code. */
export const buildPromotionDiscountByTaxCodeForCart = (cartItems) => {
  const byCode = {};
  let total = 0;

  if (!Array.isArray(cartItems)) return { byCode, total };

  cartItems.forEach((item) => {
    if (isCartPromoFreeLine(item)) return;
    const goods = getExVatGoodsValueForTotals(item);
    if (goods <= 0.0001) return;
    const discount = resolveCartLinePromotionDiscountAmount(item);
    if (discount <= 0.0001) return;
    const amount = Math.min(discount, goods);
    const code = String(resolveProductTaxCode(item) || "0");
    byCode[code] = (byCode[code] || 0) + amount;
    total += amount;
  });

  return { byCode, total };
};

export const computeTelecallerVatBreakdownTotals = (cartItems, bundleDiscounts = []) => {
  const vatBreakdown = buildVatBreakdownFromCartItems(cartItems);
  const { byCode: promoNetByCode, total: promoNetTotal } =
    buildPromotionDiscountByTaxCodeForCart(cartItems);
  // Prefer backend promotion_discount; fall back to bundle pools only when absent.
  const bundleNetByCode =
    promoNetTotal > 0.0001
      ? promoNetByCode
      : buildBundleAllocationByTaxCodeForCart(bundleDiscounts, cartItems, vatBreakdown);

  let zeroGoods = 0;
  let stdGoods = 0;
  let vatTotal = 0;

  const codes = Object.keys(vatBreakdown).sort((a, b) => {
    const na = Number(a);
    const nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    return String(a).localeCompare(String(b));
  });

  codes.forEach((code) => {
    const entry = vatBreakdown[code];
    const rate = parseFloat(entry.rate || 0) || 0;
    const grossNet = parseFloat(entry.net || 0) || 0;
    const bundleNet = parseCartBundleAmount(bundleNetByCode[code]);
    const net = Math.max(0, grossNet - bundleNet);
    const vat = (net * rate) / 100;

    if (rate <= 0.0001) {
      zeroGoods += net;
    } else {
      stdGoods += net;
    }
    vatTotal += vat;
  });

  const zeroGoodsRounded = roundCartAmount(zeroGoods);
  const stdGoodsRounded = roundCartAmount(stdGoods);
  const vatRounded = roundCartAmount(vatTotal);
  const grandTotal = roundCartAmount(zeroGoodsRounded + stdGoodsRounded + vatRounded);

  return {
    zeroGoods: zeroGoodsRounded,
    stdGoods: stdGoodsRounded,
    vat: vatRounded,
    grandTotal,
  };
};

export const EMPTY_CART_TOTALS = {
  boxCount: 0,
  pieceCount: 0,
  freeItemsCount: 0,
  promotedItemsCount: 0,
  discountedItemsCount: 0,
  zeroGoods: 0,
  stdGoods: 0,
  gross: 0,
  promoDiscPercent: 0,
  promoDiscountTotal: 0,
  lineDiscountTotal: 0,
  promoDisc: 0,
  promoVat: 0,
  promoDiscountAmount: 0,
  promoVatAmount: 0,
  subtotal: 0,
  vat: 0,
  totalBeforeDiscount: 0,
  orderDiscountPercent: 0,
  discountAmount: 0,
  finalSubtotal: 0,
  finalVat: 0,
  grandTotal: 0,
  itemDiscounts: new Map(),
};

export const selectCartDisplayTotals = (
  cartItems,
  orderDiscountValue = 0,
  orderDiscountType = "percentage",
  cartResponse = null,
  bundleDiscounts = [],
  useTelecallerVatBreakdown = false
) => {
  if (!Array.isArray(cartItems)) {
    return {
      ...EMPTY_CART_TOTALS,
      itemDiscounts: new Map(),
    };
  }

  const totals = calculateCartTotals(cartItems, orderDiscountValue, orderDiscountType);

  if (useTelecallerVatBreakdown) {
    const telecallerTotals = computeTelecallerVatBreakdownTotals(
      cartItems,
      bundleDiscounts
    );
    return {
      ...totals,
      stdGoods: telecallerTotals.stdGoods,
      zeroGoods: telecallerTotals.zeroGoods,
      vat: telecallerTotals.vat,
      grandTotal: telecallerTotals.grandTotal,
      gross: 0,
      promoDisc: 0,
      promoVat: 0,
      promoDiscountAmount: 0,
      promoVatAmount: 0,
    };
  }

  const { stdGoods, zeroGoods: zeroGoodsFromLines } = cartItems.reduce(
    (acc, item) => {
      if (isCartPromoFreeLine(item)) return acc;
      const n = getCartItemNormalizedFields(item);
      const exVat = getExVatGoodsValueForTotals(item);
      if (exVat <= 0) return acc;
      const hasVatItem = n.vatRate > 0 || n.lineVat > 0;
      if (hasVatItem) {
        acc.stdGoods += exVat;
      } else {
        acc.zeroGoods += exVat;
      }
      return acc;
    },
    { stdGoods: 0, zeroGoods: 0 }
  );
  const zeroGoods = zeroGoodsFromLines;
  const vatTotal = cartItems.reduce((sum, item) => {
    if (isCartPromoFreeLine(item)) return sum;
    const n = getCartItemNormalizedFields(item);
    if (Number.isFinite(n.lineVat) && n.lineVat >= 0) {
      return sum + n.lineVat;
    }
    return sum + (calculateItemVat(item) || 0);
  }, 0);

  const linePromo = buildPromotionDiscountByTaxCodeForCart(cartItems);
  let promoDisc = linePromo.total;
  let promoVat = 0;
  cartItems.forEach((item) => {
    if (isCartPromoFreeLine(item)) return;
    if (getExVatGoodsValueForTotals(item) <= 0.0001) return;
    const n = getCartItemNormalizedFields(item);
    promoVat += n.promoDiscVat || 0;
  });

  if (promoDisc <= 0.0001 && promoVat <= 0.0001) {
    const bundleDiscountsForTotals = Array.isArray(bundleDiscounts) ? bundleDiscounts : [];
    bundleDiscountsForTotals.forEach((b) => {
      promoDisc += resolveBundlePromoNetExcludingGet(b);
      promoVat += resolveBundlePromoVatExcludingGet(b);
    });
  }

  if (promoDisc <= 0.0001 && promoVat <= 0.0001) {
    const r = cartResponse && typeof cartResponse === "object" ? cartResponse : {};
    const rDisc =
      parseFloat(r.promoDiscount ?? r.promo_discount_amount ?? 0) || 0;
    const rVat =
      parseFloat(r.promo_vat ?? r.total_promo_discount_vat ?? r.promoVat ?? r.promo_vat_amount ?? 0) || 0;
    if (rDisc > 0.0001 || rVat > 0.0001) {
      promoDisc = rDisc;
      promoVat = rVat;
    }
  }

  const normalizedVatTotal = parseFloat(vatTotal.toFixed(2));
  const gross = parseFloat((stdGoods + zeroGoods + normalizedVatTotal).toFixed(2));
  const orderDiscountAmount = parseFloat(totals.discountAmount || 0);
  const hasPromoVat = Number.isFinite(promoVat) && promoVat > 0.0001;
  const vatAdjustFromGrand = hasPromoVat ? promoVat : 0;
  const grandBeforeBundle = gross - orderDiscountAmount;
  const grandTotal = Math.max(0, grandBeforeBundle - promoDisc - vatAdjustFromGrand);

  return {
    ...totals,
    stdGoods: parseFloat(stdGoods.toFixed(2)),
    zeroGoods: parseFloat(zeroGoods.toFixed(2)),
    vat: normalizedVatTotal,
    gross,
    promoDisc,
    promoVat,
    promoDiscountAmount: promoDisc,
    promoVatAmount: promoVat,
    grandTotal,
  };
};

const isTelecallerBuyBundleDiscountLine = (line, groupKey) => {
  if (!groupKey || promoGroupKeyForItem(line) !== groupKey) return false;
  const promoType = normalizedPromoTypeForDedupe(line);
  if (promoType === "GET" || promoType === "FREE") return false;
  if (isCartLineOfferDisplayRole(line)) return false;
  return isPromotedBuyPoolLine(line);
};

const resolveBuyLineBundleAllocationSubtotal = (line) => {
  const qty = lineQtyForPromoLabel(line);
  if (qty <= 0) return 0;

  for (const key of ["line_subtotal", "subtotal", "sub_total", "set_subtotal"]) {
    const amount = parseCartBundleAmount(line[key]);
    if (amount > 0.0001) return roundCartAmount(amount);
  }

  return resolveCartLineDisplayTotal(line, qty);
};

const resolveBuyLineBundleAllocationWeight = (line, buyQuantity, qualifyingQtyOverride = null) => {
  const qty = lineQtyForPromoLabel(line);
  if (qty <= 0) return 0;

  const qualQty =
    qualifyingQtyOverride !== null && qualifyingQtyOverride !== undefined
      ? qualifyingQtyOverride
      : buyQuantity > 0
        ? Math.min(qty, buyQuantity)
        : qty;
  if (qualQty <= 0) return 0;

  const orderType = line.order_type || line.type || "Box";
  const promoUnit = parseCartBundleAmount(line.promo_unit_price);
  const unitPrice =
    promoUnit > 0.0001
      ? promoUnit
      : resolveOfferOrSalesUnitPrice(line, orderType);

  return roundCartAmount(qualQty * unitPrice);
};

const allocateBuyPoolQualifyingQty = (lines, buyQuantity, stickySets) => {
  const allocation = new Map();
  if (!Array.isArray(lines) || lines.length === 0 || buyQuantity <= 0 || stickySets <= 0) {
    return allocation;
  }

  let remaining = stickySets * buyQuantity;
  const ordered = lines
    .map((line, index) => ({
      line,
      index,
      qty: lineQtyForPromoLabel(line),
      unit: resolvePromoUnitPriceForLine(line),
    }))
    .filter((entry) => entry.qty > 0 && entry.unit > 0.0001)
    .sort((a, b) => b.unit - a.unit || a.index - b.index);

  ordered.forEach(({ line, qty }) => {
    const key = buildStickyBundleLineKey(line);
    if (remaining <= 0) {
      allocation.set(key, 0);
      return;
    }
    const qualQty = Math.min(qty, remaining);
    allocation.set(key, qualQty);
    remaining -= qualQty;
  });

  lines.forEach((line) => {
    const key = buildStickyBundleLineKey(line);
    if (!allocation.has(key)) allocation.set(key, 0);
  });

  return allocation;
};

const computeLineSetBasedPromoDiscount = (line, qualifyingQty, discountRate) => {
  if (qualifyingQty <= 0 || discountRate <= 0.0001) return 0;
  const unit = resolvePromoUnitPriceForLine(line);
  if (unit <= 0.0001) return 0;
  return roundCartAmount(qualifyingQty * unit * (discountRate / 100));
};

const resolveQualifyingAllocationWeights = (lines, buyQuantity, stickySets) => {
  const allocation = allocateBuyPoolQualifyingQty(lines, buyQuantity, stickySets);
  return lines.map((line) => {
    const qualQty = allocation.get(buildStickyBundleLineKey(line)) || 0;
    return resolveBuyLineBundleAllocationWeight(line, 0, qualQty);
  });
};

const splitBundleDiscountByQualifyingAllocation = (
  totalDiscount,
  lines,
  buyQuantity,
  stickySets
) => {
  const map = new Map();
  const safeTotal = roundCartAmount(totalDiscount);
  if (safeTotal <= 0.0001 || !Array.isArray(lines) || lines.length === 0) return map;

  const weights = resolveQualifyingAllocationWeights(lines, buyQuantity, stickySets);
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (totalWeight <= 0.0001) return map;

  lines.forEach((line, index) => {
    const amount = allocateBundleAmountBySetWeights(safeTotal, weights, index);
    if (amount > 0.0001) {
      map.set(buildStickyBundleLineKey(line), amount);
    }
  });

  return map;
};

const computeTelecallerSetBasedFixedBundleDiscount = (
  lines,
  buyQuantity,
  stickySets,
  setOfferPrice
) => {
  if (
    !Array.isArray(lines) ||
    lines.length === 0 ||
    stickySets <= 0 ||
    buyQuantity <= 0 ||
    setOfferPrice <= 0.0001
  ) {
    return 0;
  }

  const allocation = allocateBuyPoolQualifyingQty(lines, buyQuantity, stickySets);
  let qualifyingSubtotal = 0;
  lines.forEach((line) => {
    const qualQty = allocation.get(buildStickyBundleLineKey(line)) || 0;
    if (qualQty <= 0) return;
    qualifyingSubtotal += qualQty * resolvePromoUnitPriceForLine(line);
  });

  if (qualifyingSubtotal <= 0.0001) return 0;
  return roundCartAmount(qualifyingSubtotal - stickySets * setOfferPrice);
};

const resolveLineSetBasedPromoDiscount = (item, cartItems, buyQuantity, stickySets) => {
  const promoId = resolveCartPromoId(item);
  const lines = resolvePromotionBuyPoolLines(cartItems, promoId);
  if (lines.length === 0 || buyQuantity <= 0 || stickySets <= 0) return 0;

  const discountRate = resolvePromoDiscountRateForLines(lines);
  if (discountRate <= 0.0001) return 0;

  const allocation = allocateBuyPoolQualifyingQty(lines, buyQuantity, stickySets);
  const qualQty = allocation.get(buildStickyBundleLineKey(item)) || 0;
  return computeLineSetBasedPromoDiscount(item, qualQty, discountRate);
};

/** Split bundle discount by weight (subtotal share). Rounds to cents; remainder goes to largest fractions. */
const allocateBundleAmountBySetWeights = (total, weights, linePosition) => {
  const safeTotal = roundCartAmount(total);
  const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
  if (safeTotal <= 0.0001 || totalWeight <= 0 || linePosition < 0) return 0;
  if (weights.length <= 1 || totalWeight <= 1) return safeTotal;

  const totalCents = Math.round(safeTotal * 100);
  const rawShares = weights.map((weight) => (totalCents * weight) / totalWeight);
  const floored = rawShares.map((share) => Math.floor(share));
  let remainder = totalCents - floored.reduce((sum, share) => sum + share, 0);

  const cents = [...floored];
  const fractionOrder = rawShares
    .map((share, index) => ({ index, fraction: share - floored[index] }))
    .sort((a, b) => b.fraction - a.fraction);

  for (let i = 0; i < remainder; i += 1) {
    cents[fractionOrder[i % fractionOrder.length].index] += 1;
  }

  return cents[linePosition] / 100;
};

const resolvePromoBundlePartsForItem = (item, bundleDiscounts = []) => {
  if (!item || !Array.isArray(bundleDiscounts)) {
    return { buyPart: 0, getPart: 0, fallbackPart: 0, fullTotal: 0 };
  }

  const promoId = String(
    item.promotion_id ??
      item.promotionId ??
      item.promo_id ??
      item.promosId ??
      item.promoId ??
      ""
  ).trim();
  if (!promoId) {
    return { buyPart: 0, getPart: 0, fallbackPart: 0, fullTotal: 0 };
  }

  let buyPart = 0;
  let getPart = 0;
  let fallbackPart = 0;

  bundleDiscounts.forEach((bundle) => {
    if (String(bundle.promotion_id ?? "").trim() !== promoId) return;

    const bundleBuy = parseCartBundleAmount(bundle.buy_total_discount);
    const bundleGet = parseCartBundleAmount(bundle.get_total_discount);
    if (bundleBuy + bundleGet > 0.0001) {
      buyPart += bundleBuy;
      getPart += bundleGet;
      return;
    }

    const tpm = parseCartBundleAmount(bundle.total_promo_discount);
    const td = parseCartBundleAmount(bundle.total_discount);
    fallbackPart += tpm > 0.0001 ? tpm : td;
  });

  const fullTotal =
    buyPart + getPart > 0.0001
      ? buyPart + getPart
      : fallbackPart;

  return {
    buyPart: roundCartAmount(buyPart),
    getPart: roundCartAmount(getPart),
    fallbackPart: roundCartAmount(fallbackPart),
    fullTotal: roundCartAmount(fullTotal),
  };
};

export const buildStickyBundleLineKey = (item) => {
  const promoId = resolveCartPromoId(item);
  const productId = String(item?.product_id ?? item?.productId ?? item?.id ?? "");
  const orderType = String(item?.order_type ?? item?.type ?? "");
  return `${promoId}::${productId}::${orderType}`;
};

export const createStickyBundleDiscountState = () => ({
  entries: new Map(),
});

export const resetStickyBundleDiscountState = (state) => {
  if (state?.entries) state.entries.clear();
};

export const resolveCartPromoBundleDiscountNet = (
  item,
  bundleDiscounts = [],
  options = {}
) => {
  const {
    cartItems = null,
    distributeBundleAmongBuyLines = false,
    lineDiscountByKey = null,
    buyQuantity = 0,
    stickySets = 0,
  } = options;

  if (lineDiscountByKey instanceof Map) {
    const cached = lineDiscountByKey.get(buildStickyBundleLineKey(item));
    if (cached !== undefined && cached > 0.0001) {
      return roundCartAmount(cached);
    }
  }

  const hasBundleDiscountSource =
    Array.isArray(bundleDiscounts) && bundleDiscounts.length > 0;

  if (distributeBundleAmongBuyLines && !hasBundleDiscountSource) {
    return 0;
  }

  if (
    distributeBundleAmongBuyLines &&
    Array.isArray(cartItems) &&
    buyQuantity > 0 &&
    stickySets > 0 &&
    hasBundleDiscountSource
  ) {
    const direct = resolveLineSetBasedPromoDiscount(item, cartItems, buyQuantity, stickySets);
    if (direct > 0.0001) return direct;
  }

  const { buyPart, getPart, fullTotal } = resolvePromoBundlePartsForItem(
    item,
    bundleDiscounts
  );
  if (fullTotal <= 0.0001) return 0;

  const promoType = normalizedPromoTypeForDedupe(item);
  const isGetFreeLine =
    promoType === "GET" ||
    promoType === "FREE" ||
    isCartLineOfferDisplayRole(item);

  if (isGetFreeLine) {
    if (getPart > 0.0001) return getPart;
    return fullTotal;
  }

  const buyPool =
    buyPart > 0.0001 ? buyPart : getPart <= 0.0001 ? fullTotal : buyPart;

  if (
    !distributeBundleAmongBuyLines ||
    !Array.isArray(cartItems) ||
    buyPool <= 0.0001
  ) {
    return buyPool > 0.0001 ? buyPool : fullTotal;
  }

  const groupKey = promoGroupKeyForItem(item);
  const promoId = resolveCartPromoId(item);
  const poolLines =
    stickySets > 0 && buyQuantity > 0 && promoId
      ? resolvePromotionBuyPoolLines(cartItems, promoId)
      : [];
  const qualifyingAllocation =
    poolLines.length > 0
      ? allocateBuyPoolQualifyingQty(poolLines, buyQuantity, stickySets)
      : null;
  const eligible = cartItems
    .map((line, index) => ({ line, index }))
    .filter(({ line }) => isTelecallerBuyBundleDiscountLine(line, groupKey))
    .map((entry) => {
      let subtotal = 0;
      if (qualifyingAllocation) {
        const qualQty =
          qualifyingAllocation.get(buildStickyBundleLineKey(entry.line)) || 0;
        subtotal = resolveBuyLineBundleAllocationWeight(entry.line, 0, qualQty);
      } else if (buyQuantity > 0) {
        subtotal = resolveBuyLineBundleAllocationWeight(entry.line, buyQuantity);
      } else {
        subtotal = resolveBuyLineBundleAllocationSubtotal(entry.line);
      }
      return { ...entry, subtotal };
    })
    .filter((entry) => entry.subtotal > 0.0001);

  if (eligible.length <= 1) {
    return buyPool > 0.0001 ? buyPool : fullTotal;
  }

  const linePosition = eligible.findIndex(({ line }) => line === item);
  if (linePosition < 0) {
    return buyPool > 0.0001 ? buyPool : fullTotal;
  }

  const subtotalWeights = eligible.map((entry) => entry.subtotal);
  return allocateBundleAmountBySetWeights(buyPool, subtotalWeights, linePosition);
};

const resolvePromoGroupKeyForPromotionId = (cartItems, promoId) => {
  const promoIdStr = String(promoId ?? "").trim();
  if (!promoIdStr || !Array.isArray(cartItems)) return "";
  for (const item of cartItems) {
    if (resolveCartPromoId(item) === promoIdStr) {
      return promoGroupKeyForItem(item);
    }
  }
  return "";
};

const resolvePromotionBuyPoolLines = (cartItems, promoId) => {
  const promoIdStr = String(promoId ?? "").trim();
  if (!promoIdStr || !Array.isArray(cartItems)) return [];
  return cartItems.filter((line) => {
    if (resolveCartPromoId(line) !== promoIdStr) return false;
    const groupKey = promoGroupKeyForItem(line);
    return isTelecallerBuyBundleDiscountLine(line, groupKey);
  });
};

const buildPromotionBuyPoolStructureKey = (cartItems, promoId) =>
  resolvePromotionBuyPoolLines(cartItems, promoId)
    .map((line) => {
      const productId = String(line.product_id ?? line.productId ?? line.id ?? "");
      const orderType = String(line.order_type ?? line.type ?? "");
      return `${productId}:${orderType}`;
    })
    .sort()
    .join("|");

const buildPromotionBuyPoolQtyKey = (cartItems, promoId) =>
  resolvePromotionBuyPoolLines(cartItems, promoId)
    .map((line) => {
      const productId = String(line.product_id ?? line.productId ?? line.id ?? "");
      const orderType = String(line.order_type ?? line.type ?? "");
      const qty = lineQtyForPromoLabel(line);
      return `${productId}:${orderType}:${qty}`;
    })
    .sort()
    .join("|");

const buildPromotionBracketKey = (structureKey, stickySets) =>
  `${structureKey}::${stickySets}`;

const createPromoStickyEntry = () => ({
  poolSnapshots: new Map(),
  tierPartialSnapshots: new Map(),
});

const applyPromoStickySnapshot = (snapshot, lineDiscountByKey) => {
  snapshot.lineDiscountByKey.forEach((value, key) => lineDiscountByKey.set(key, value));
  return snapshot.bundle;
};

const hasPromotionPartialSetRemainder = (cartItems, promoId, buyQuantity) => {
  if (buyQuantity <= 0) return false;
  const lines = resolvePromotionBuyPoolLines(cartItems, promoId);
  const totalQty = lines.reduce((sum, line) => sum + lineQtyForPromoLabel(line), 0);
  return totalQty > 0 && totalQty % buyQuantity !== 0;
};

const resolvePromoUnitPriceForLine = (line) => {
  const orderType = line.order_type || line.type || "Box";
  const promoUnit = parseCartBundleAmount(line.promo_unit_price);
  if (promoUnit > 0.0001) return promoUnit;
  return resolveOfferOrSalesUnitPrice(line, orderType);
};

const resolvePromoDiscountRateForLines = (lines) => {
  if (!Array.isArray(lines) || lines.length === 0) return 0;

  for (const line of lines) {
    for (const key of [
      "discount_percentage",
      "promo_disc_percentage",
      "promo_discount_percentage",
    ]) {
      const pct = parseFloat(line?.[key] ?? 0);
      if (pct > 0.0001) return pct;
    }
  }

  for (const line of lines) {
    const name = promotionNameForLabelKey(line);
    const match = name.match(/(\d+(?:\.\d+)?)\s*%/);
    if (match) {
      const pct = parseFloat(match[1]);
      if (pct > 0.0001) return pct;
    }
  }

  return 0;
};

const computeTelecallerSetBasedBuyPool = (lines, buyQuantity, stickySets, discountRate) => {
  if (!Array.isArray(lines) || lines.length === 0 || stickySets <= 0 || buyQuantity <= 0) {
    return 0;
  }

  if (discountRate > 0.0001) {
    const allocation = allocateBuyPoolQualifyingQty(lines, buyQuantity, stickySets);
    let total = 0;
    lines.forEach((line) => {
      const key = buildStickyBundleLineKey(line);
      const qualQty = allocation.get(key) || 0;
      total += computeLineSetBasedPromoDiscount(line, qualQty, discountRate);
    });
    return roundCartAmount(total);
  }

  const setOfferPrice = resolveFixedSetOfferPriceForLines(lines);
  if (setOfferPrice > 0.0001) {
    return computeTelecallerSetBasedFixedBundleDiscount(
      lines,
      buyQuantity,
      stickySets,
      setOfferPrice
    );
  }

  return 0;
};

const applyClientSetBasedBuyPoolToBundle = (
  bundle,
  cartItems,
  promoId,
  buyQuantity,
  stickySets
) => {
  const lines = resolvePromotionBuyPoolLines(cartItems, promoId);
  if (lines.length === 0 || buyQuantity <= 0 || stickySets <= 0) {
    return bundle;
  }

  const discountRate = resolvePromoDiscountRateForLines(lines);
  const setOfferPrice = resolveFixedSetOfferPriceForLines(lines);
  const clientBuyPool = computeTelecallerSetBasedBuyPool(
    lines,
    buyQuantity,
    stickySets,
    discountRate
  );

  if (clientBuyPool <= 0.0001 && setOfferPrice <= 0.0001 && discountRate <= 0.0001) {
    return bundle;
  }

  if (clientBuyPool <= 0.0001) return bundle;

  return {
    ...bundle,
    buy_total_discount: String(clientBuyPool),
    sets_count: String(stickySets),
  };
};

const buildPromotionLineDiscountMap = (bundle, cartItems, buyQuantity = 0, stickySets = 0) => {
  const map = new Map();
  const promoId = String(bundle?.promotion_id ?? "").trim();
  if (!promoId || !Array.isArray(cartItems)) return map;

  const lines = resolvePromotionBuyPoolLines(cartItems, promoId);
  const effectiveSets =
    stickySets > 0
      ? stickySets
      : resolveRawPromotionSetsCount(cartItems, promoId, buyQuantity, bundle);
  const discountRate = resolvePromoDiscountRateForLines(lines);
  const setOfferPrice = resolveFixedSetOfferPriceForLines(lines);

  if (buyQuantity > 0 && effectiveSets > 0 && lines.length > 0) {
    if (discountRate > 0.0001) {
      const allocation = allocateBuyPoolQualifyingQty(lines, buyQuantity, effectiveSets);
      lines.forEach((line) => {
        const key = buildStickyBundleLineKey(line);
        const qualQty = allocation.get(key) || 0;
        const amount = computeLineSetBasedPromoDiscount(line, qualQty, discountRate);
        if (amount > 0.0001) {
          map.set(key, amount);
        }
      });
      return map;
    }

    if (setOfferPrice > 0.0001) {
      const totalDiscount = computeTelecallerSetBasedFixedBundleDiscount(
        lines,
        buyQuantity,
        effectiveSets,
        setOfferPrice
      );
      return splitBundleDiscountByQualifyingAllocation(
        totalDiscount,
        lines,
        buyQuantity,
        effectiveSets
      );
    }

    const buyPool = parseCartBundleAmount(bundle?.buy_total_discount);
    if (buyPool > 0.0001) {
      return splitBundleDiscountByQualifyingAllocation(
        buyPool,
        lines,
        buyQuantity,
        effectiveSets
      );
    }
  }

  cartItems.forEach((item) => {
    if (resolveCartPromoId(item) !== promoId) return;
    const amount = resolveCartPromoBundleDiscountNet(item, [bundle], {
      cartItems,
      distributeBundleAmongBuyLines: true,
      buyQuantity,
      stickySets: effectiveSets,
    });
    if (amount > 0.0001) {
      map.set(buildStickyBundleLineKey(item), roundCartAmount(amount));
    }
  });

  return map;
};

const resolveRawPromotionSetsCount = (cartItems, promoId, buyQuantity, bundle) => {
  const lines = resolvePromotionBuyPoolLines(cartItems, promoId);
  if (lines.length === 0) {
    return parseBundleSetsCount(bundle);
  }

  const totalBuyQty = lines.reduce((sum, line) => sum + lineQtyForPromoLabel(line), 0);
  if (buyQuantity > 0 && totalBuyQty > 0) {
    return Math.floor(totalBuyQty / buyQuantity);
  }

  return parseBundleSetsCount(bundle);
};

export const resolveCartPromoStickySetsForItem = (item, cartItems, bundleDiscounts = []) => {
  const promoId = resolveCartPromoId(item);
  if (!promoId || !Array.isArray(cartItems)) return 0;
  const groupKey = promoGroupKeyForItem(item);
  const buyQuantity = groupKey
    ? resolveBuyQuantityForPromo(cartItems, groupKey, bundleDiscounts)
    : 0;
  const bundle = (Array.isArray(bundleDiscounts) ? bundleDiscounts : []).find(
    (entry) => String(entry?.promotion_id ?? "").trim() === promoId
  );
  return resolveRawPromotionSetsCount(cartItems, promoId, buyQuantity, bundle);
};

const resolveStickyPromotionSetsCount = ({ rawSets }) => rawSets;

const scaleBundleDiscountNumericField = (value, scale) =>
  String(roundCartAmount(parseCartBundleAmount(value) * scale));

const scaleBundleDiscountEntry = (bundle, scale, stickySets) => {
  if (!bundle || Math.abs(scale - 1) < 0.0001) {
    return {
      ...bundle,
      sets_count: String(stickySets),
    };
  }

  return {
    ...bundle,
    sets_count: String(stickySets),
    buy_total_discount: scaleBundleDiscountNumericField(bundle.buy_total_discount, scale),
    get_total_discount: scaleBundleDiscountNumericField(bundle.get_total_discount, scale),
    total_promo_discount: scaleBundleDiscountNumericField(bundle.total_promo_discount, scale),
    total_discount: scaleBundleDiscountNumericField(bundle.total_discount, scale),
    buy_discount_vat: scaleBundleDiscountNumericField(bundle.buy_discount_vat, scale),
    get_discount_vat: scaleBundleDiscountNumericField(bundle.get_discount_vat, scale),
    total_promo_discount_vat: scaleBundleDiscountNumericField(
      bundle.total_promo_discount_vat,
      scale
    ),
    promo_discount_vat: scaleBundleDiscountNumericField(bundle.promo_discount_vat, scale),
    discount_vat: scaleBundleDiscountNumericField(bundle.discount_vat, scale),
    set_subtotal: scaleBundleDiscountNumericField(bundle.set_subtotal, scale),
    get_set_subtotal: scaleBundleDiscountNumericField(bundle.get_set_subtotal, scale),
  };
};

export const applyStickyBundleDiscountPolicy = (bundleDiscounts, cartItems, state) => {
  if (!Array.isArray(bundleDiscounts) || bundleDiscounts.length === 0) {
    return { bundleDiscounts: [], lineDiscountByKey: null };
  }
  if (!Array.isArray(cartItems) || cartItems.length === 0) {
    return { bundleDiscounts, lineDiscountByKey: null };
  }

  const stickyState = state || createStickyBundleDiscountState();
  const lineDiscountByKey = new Map();

  const adjustedBundles = bundleDiscounts.map((bundle) => {
    const promoId = String(bundle.promotion_id ?? "").trim();
    if (!promoId) return bundle;

    const groupKey = resolvePromoGroupKeyForPromotionId(cartItems, promoId);
    const buyQuantity = groupKey
      ? resolveBuyQuantityForPromo(cartItems, groupKey, bundleDiscounts)
      : 0;
    const rawSets = resolveRawPromotionSetsCount(cartItems, promoId, buyQuantity, bundle);
    const structureKey = buildPromotionBuyPoolStructureKey(cartItems, promoId);
    const poolQtyKey = buildPromotionBuyPoolQtyKey(cartItems, promoId);
    const stickySets = resolveStickyPromotionSetsCount({ rawSets });
    const tierKey = buildPromotionBracketKey(structureKey, stickySets);
    const hasPartial = hasPromotionPartialSetRemainder(cartItems, promoId, buyQuantity);

    let promoEntry = stickyState.entries.get(promoId);
    if (!promoEntry?.poolSnapshots) {
      promoEntry = createPromoStickyEntry();
    }

    const poolSnapshot = promoEntry.poolSnapshots.get(poolQtyKey);
    if (poolSnapshot && poolSnapshot.stickySets === stickySets) {
      stickyState.entries.set(promoId, promoEntry);
      return applyPromoStickySnapshot(poolSnapshot, lineDiscountByKey);
    }

    if (hasPartial && promoEntry.tierPartialSnapshots.has(tierKey)) {
      const tierSnapshot = promoEntry.tierPartialSnapshots.get(tierKey);
      stickyState.entries.set(promoId, promoEntry);
      return applyPromoStickySnapshot(tierSnapshot, lineDiscountByKey);
    }

    const apiSets = parseBundleSetsCount(bundle);
    const apiAligned = apiSets <= 0 || apiSets === stickySets;
    const scaleBase = apiSets > 0 ? apiSets : Math.max(rawSets, stickySets, 1);
    const scale = scaleBase > 0 ? stickySets / scaleBase : 1;
    let scaledBundle = scaleBundleDiscountEntry(bundle, scale, stickySets);
    scaledBundle = applyClientSetBasedBuyPoolToBundle(
      scaledBundle,
      cartItems,
      promoId,
      buyQuantity,
      stickySets
    );

    const perLineMap = buildPromotionLineDiscountMap(
      scaledBundle,
      cartItems,
      buyQuantity,
      stickySets
    );
    perLineMap.forEach((value, key) => lineDiscountByKey.set(key, value));

    const snapshot = {
      stickySets,
      bundle: scaledBundle,
      lineDiscountByKey: perLineMap,
    };

    if (apiAligned) {
      promoEntry.poolSnapshots.set(poolQtyKey, snapshot);
      if (hasPartial) {
        if (!promoEntry.tierPartialSnapshots.has(tierKey)) {
          promoEntry.tierPartialSnapshots.set(tierKey, snapshot);
        }
      } else {
        promoEntry.tierPartialSnapshots.set(tierKey, snapshot);
      }
    }

    stickyState.entries.set(promoId, promoEntry);
    return scaledBundle;
  });

  return {
    bundleDiscounts: adjustedBundles,
    lineDiscountByKey: lineDiscountByKey.size > 0 ? lineDiscountByKey : null,
  };
};

/**
 * Line-level promo discount as sent by the backend. `promotion_discount` is
 * preferred; check-promotion names the same value `promo_disc_amount` /
 * `line_discount` / `discount_amount`. Only read for promoted lines so manual
 * discounts are never mistaken for a promotion.
 */
export const resolveCartLinePromotionDiscountAmount = (item) => {
  if (!item) return 0;

  if (!isActivePromotedLineForDiscountAllocation(item)) {
    return 0;
  }

  const explicit = parseCartBundleAmount(item.promotion_discount);
  if (explicit > 0.0001) return roundCartAmount(explicit);

  for (const key of [
    "promo_disc_amount",
    "promo_discount_amount",
    "promo_discount",
    "line_discount",
    "discount_amount",
    "savings",
  ]) {
    const amount = parseCartBundleAmount(item[key]);
    if (amount > 0.0001) return roundCartAmount(amount);
  }

  if (resolveManualItemDiscountValue(item) > 0.0001) {
    return 0;
  }

  const promoPercent = parseCartBundleAmount(
    item.promo_disc_percentage ?? item.promo_discount_percentage
  );
  if (promoPercent > 0.0001) {
    const lineGross = parseCartBundleAmount(
      item.line_subtotal ?? item.sub_total ?? item.subtotal
    );
    if (lineGross > 0.0001) {
      return roundCartAmount((lineGross * promoPercent) / 100);
    }
  }

  return 0;
};

/** Discount shown on promotion sub-row: backend promo discount only. */
export const resolveCartPromoRowDiscountNet = (item, _bundleDiscounts = [], _options = {}) => {
  return resolveCartLinePromotionDiscountAmount(item);
};

export const resolveCartLineInlinePromoGrossDisplayTotal = (netTotal) => {
  return parseFloat(Number(netTotal).toFixed(2));
};

const resolveBundleSetsCountForItem = (item, bundleDiscounts = []) => {
  const promoId = resolveCartPromoId(item);
  if (!promoId || !Array.isArray(bundleDiscounts)) return 0;

  for (const bundle of bundleDiscounts) {
    if (String(bundle?.promotion_id ?? "").trim() !== promoId) continue;
    const sets = parseBundleSetsCount(bundle);
    if (sets > 0) return sets;
  }

  return 0;
};

const countShowPromoBuyLinesInGroup = (item, cartItems = []) => {
  const groupKey = promoGroupKeyForItem(item);
  if (!groupKey || !Array.isArray(cartItems)) return 0;

  let count = 0;
  cartItems.forEach((line) => {
    if (line?.showPromo !== true) return;
    if (promoGroupKeyForItem(line) !== groupKey) return;
    if (!isPromotedBuyPoolLine(line)) return;
    count += 1;
  });
  return count;
};

const resolveCartPromoSetsForDisplay = (item, bundleDiscounts = [], cartItems = null) => {
  const apiSets = resolveBundleSetsCountForItem(item, bundleDiscounts);
  const labelCount = countShowPromoBuyLinesInGroup(item, cartItems);
  const buyQuantity = resolveBuyQuantityForCartPromoGroup(item, cartItems, bundleDiscounts);
  const groupKey = promoGroupKeyForItem(item);

  let qtySets = 0;
  if (groupKey && buyQuantity > 0 && Array.isArray(cartItems)) {
    const totalBuyQty = cartItems.reduce((sum, line) => {
      if (promoGroupKeyForItem(line) !== groupKey) return sum;
      if (!isPromotedBuyPoolLine(line)) return sum;
      return sum + lineQtyForPromoLabel(line);
    }, 0);
    qtySets = Math.floor(totalBuyQty / buyQuantity);
  }

  return Math.max(apiSets, labelCount, qtySets, 1);
};

const resolveCartPromoGroupBuyDiscountTotal = (
  item,
  bundleDiscounts = [],
  cartItems = null
) => {
  const groupKey = promoGroupKeyForItem(item);
  if (groupKey && Array.isArray(cartItems)) {
    let sum = 0;
    cartItems.forEach((line) => {
      if (promoGroupKeyForItem(line) !== groupKey) return;
      if (!isPromotedBuyPoolLine(line)) return;
      sum += resolveCartLinePromotionDiscountAmount(line);
    });
    if (sum > 0.0001) return roundCartAmount(sum);

    const hasFreeGetInGroup = cartItems.some((line) => {
      if (promoGroupKeyForItem(line) !== groupKey) return false;
      const promoType = normalizedPromoTypeForDedupe(line);
      if (promoType === "FREE") return true;
      if (promoType !== "GET" && !isCartLineOfferDisplayRole(line)) return false;
      const price = Math.max(
        parseFloat(line.price ?? 0) || 0,
        parseFloat(line.unit_price ?? 0) || 0
      );
      return price <= 0.0001;
    });
    if (hasFreeGetInGroup) {
      const { buyPart } = resolvePromoBundlePartsForItem(item, bundleDiscounts);
      return buyPart > 0.0001 ? roundCartAmount(buyPart) : 0;
    }
    return 0;
  }

  const { buyPart } = resolvePromoBundlePartsForItem(item, bundleDiscounts);
  return buyPart > 0.0001 ? buyPart : 0;
};

export const resolveCartPromotionSummaryBuyDiscountForDisplay = (
  item,
  bundleDiscounts = [],
  { cartItems = null } = {}
) => {
  if (!item || item.showPromo !== true) return 0;
  if (!isPromotedBuyPoolLine(item)) return 0;

  const totalBuyDiscount = resolveCartPromoGroupBuyDiscountTotal(
    item,
    bundleDiscounts,
    cartItems
  );
  if (totalBuyDiscount <= 0.0001) return 0;

  const groupKey = promoGroupKeyForItem(item);
  const showPromoLines = Array.isArray(cartItems)
    ? cartItems.filter(
        (line) =>
          line?.showPromo === true &&
          promoGroupKeyForItem(line) === groupKey &&
          isPromotedBuyPoolLine(line)
      )
    : [];

  if (showPromoLines.length <= 1) {
    return roundCartAmount(totalBuyDiscount);
  }

  const totalSets = resolveCartPromoSetsForDisplay(item, bundleDiscounts, cartItems);
  const perSetDiscount = roundCartAmount(totalBuyDiscount / Math.max(totalSets, 1));

  const lineSets = parseInt(item.promoDisplaySets, 10);
  if (Number.isFinite(lineSets) && lineSets > 0) {
    return roundCartAmount(perSetDiscount * lineSets);
  }

  const buyQuantity = resolveBuyQuantityForCartPromoGroup(
    item,
    cartItems,
    bundleDiscounts
  );
  const lineQty = lineQtyForPromoLabel(item);
  if (buyQuantity > 0 && lineQty >= buyQuantity) {
    const fullLineSets = Math.min(Math.floor(lineQty / buyQuantity), totalSets);
    if (fullLineSets > 0) {
      return roundCartAmount(perSetDiscount * fullLineSets);
    }
  }

  return perSetDiscount;
};

export const shouldShowCartPromotionSummarySubRow = (
  item,
  bundleDiscounts = [],
  options = {}
) => {
  if (!item || item.showPromo !== true) return false;

  const promoType = normalizedPromoTypeForDedupe(item);
  if (
    promoType === "GET" ||
    promoType === "FREE" ||
    isCartLineOfferDisplayRole(item)
  ) {
    return false;
  }

  if (!isPromotedBuyPoolLine(item)) return false;

  const promoName = String(
    item.promotion_name ?? item.promotionName ?? item.promo_name ?? ""
  ).trim();
  if (!promoName) return false;

  return (
    resolveCartPromotionSummaryBuyDiscountForDisplay(item, bundleDiscounts, options) >
    0.0001
  );
};

export const shouldShowCartLineInlinePromoDiscount = (item, cartItems = null) => {
  if (isFullyDiscountedFreeOfferLine(item)) return false;
  const linePromo = resolveCartLinePromotionDiscountAmount(item);
  if (linePromo <= 0.0001) return false;

  if (isPromotedBuyPoolLine(item)) {
    const groupKey = promoGroupKeyForItem(item);
    if (groupKey && Array.isArray(cartItems)) {
      const hasBuyPromoSummaryLine = cartItems.some(
        (line) =>
          line?.showPromo === true &&
          promoGroupKeyForItem(line) === groupKey &&
          isPromotedBuyPoolLine(line)
      );
      if (hasBuyPromoSummaryLine) return false;
    }
  }

  return true;
};

export const resolveCartLineInlinePromoDiscountForDisplay = (item) => {
  return resolveCartLinePromotionDiscountAmount(item);
};

export const buildCartPromoGroupBuckets = (cartItems) => {
  const buckets = new Map();
  if (Array.isArray(cartItems)) {
    for (const line of cartItems) {
      if (!line) continue;
      const groupKey = promoGroupKeyForItem(line);
      if (!groupKey) continue;
      if (!buckets.has(groupKey)) {
        buckets.set(groupKey, []);
      }
      buckets.get(groupKey).push(line);
    }
  }
  return buckets;
};

export const getCartPromoGroupItemsFromBuckets = (item, groupBuckets) => {
  if (Array.isArray(groupBuckets)) return groupBuckets;
  const groupKey = promoGroupKeyForItem(item);
  if (!groupKey || !groupBuckets || typeof groupBuckets.get !== "function") return null;
  return groupBuckets.get(groupKey) || null;
};

export const shouldShowCartLineInlinePromoDiscountFromBuckets = (item, groupBuckets) => {
  const items = getCartPromoGroupItemsFromBuckets(item, groupBuckets);
  return shouldShowCartLineInlinePromoDiscount(item, items);
};

export const shouldShowCartPromotionSummarySubRowFromBuckets = (item, bundleDiscounts, groupBuckets) => {
  const items = getCartPromoGroupItemsFromBuckets(item, groupBuckets);
  return shouldShowCartPromotionSummarySubRow(item, bundleDiscounts, { cartItems: items });
};

export const resolveCartPromotionSummaryBuyDiscountForDisplayFromBuckets = (item, bundleDiscounts, groupBuckets) => {
  const items = getCartPromoGroupItemsFromBuckets(item, groupBuckets);
  return resolveCartPromotionSummaryBuyDiscountForDisplay(item, bundleDiscounts, { cartItems: items });
};

export const resolveCartPromotionSummarySubRowCount = () => 1;

export const resolveCartPromotionSummarySubRowCountFromBuckets = (item, groupBuckets) => {
  const items = getCartPromoGroupItemsFromBuckets(item, groupBuckets);
  return resolveCartPromotionSummarySubRowCount(item, items);
};

/** Promo sub-row Total column: backend `promotion_discount` only (display, no main-row math). */
export const resolveCartPromoSubRowDiscountForDisplay = (
  item,
  _bundleDiscounts = [],
  _options = {}
) => resolveCartPromoRowDiscountNet(item);

export const shouldShowCartPromoBundleDiscountSubRow = () => false;

